import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Box,
  Card,
  CardBody,
  HStack,
  Heading,
  Input,
  Select,
  Button,
  VStack,
  useToast,
  Icon,
  Text,
  useDisclosure,
  Center,
  Spinner,
  IconButton,
  ButtonGroup,
  Collapse,
  Drawer,
  DrawerOverlay,
  DrawerContent,
  DrawerHeader,
  DrawerBody,
  DrawerCloseButton,
} from "@chakra-ui/react";
import { MdFileDownload, MdArrowDropDown, MdKeyboardArrowUp } from "react-icons/md";
import { FaBrain } from "react-icons/fa";
import * as XLSX from "xlsx";

const downloadCsv = (filename: string, csv: string) => {
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

const downloadWorkbookAsCsv = (workbook: XLSX.WorkBook, baseFilename: string) => {
  const base = baseFilename.replace(/\.xlsx$/i, "");
  workbook.SheetNames.forEach((sheetName, i) => {
    const csv = XLSX.utils.sheet_to_csv(workbook.Sheets[sheetName]);
    const filename = workbook.SheetNames.length === 1 ? `${base}.csv` : `${base}_${sheetName}.csv`;
    setTimeout(() => downloadCsv(filename, csv), i * 80);
  });
};

const MONTH_ABBR = [
  "JAN",
  "FEB",
  "MAR",
  "APR",
  "MAY",
  "JUN",
  "JUL",
  "AUG",
  "SEP",
  "OCT",
  "NOV",
  "DEC",
];

function formatPeriodLabel(period: string): string {
  if (period.length === 7) {
    const [year, month] = period.split("-");
    return `${MONTH_ABBR[parseInt(month, 10) - 1]} ${year}`;
  }
  return period;
}

// Pure function: Xacto-style pivot (rows=periods, cols=groups).
// periodLen=7 → monthly (YYYY-MM), periodLen=4 → yearly (YYYY).
function computeXactoPivot(
  data: any[],
  groupBy: "merchant" | "vendor" | "category" | "subcategory",
  periodLen: 4 | 7,
) {
  const monthGroupMap: Record<string, Record<string, number>> = {};
  const allGroups = new Set<string>();

  for (const t of data) {
    const period = t.date.substring(0, periodLen);
    const amount = t.amount || 0;
    const accountingAmount = -amount; // negate: income positive

    let group: string;
    if (groupBy === "merchant") group = getMerchantLabelForMatrix(t);
    else if (groupBy === "vendor") group = getVendorLabel(t);
    else if (groupBy === "subcategory")
      group =
        t.personalFinanceCategoryDetailed || t.personalFinanceCategoryPrimary || "Uncategorized";
    else group = t.personalFinanceCategoryPrimary || "Uncategorized";

    allGroups.add(group);
    if (!monthGroupMap[period]) monthGroupMap[period] = {};
    monthGroupMap[period][group] = (monthGroupMap[period][group] || 0) + accountingAmount;
  }

  const groupTotals: Record<string, number> = {};
  for (const group of allGroups) {
    groupTotals[group] = Object.values(monthGroupMap).reduce(
      (sum, pd) => sum + (pd[group] || 0),
      0,
    );
  }

  const columns = Array.from(allGroups).sort((a, b) => {
    const aIncome = (groupTotals[a] ?? 0) > 0;
    const bIncome = (groupTotals[b] ?? 0) > 0;
    if (aIncome !== bIncome) return aIncome ? -1 : 1;
    return a.localeCompare(b);
  });

  const sortedPeriods = Object.keys(monthGroupMap).sort();
  const rows = sortedPeriods.map((period) => {
    const pd = monthGroupMap[period];
    const rowTotal = columns.reduce((sum, col) => sum + (pd[col] || 0), 0);
    return { period, values: pd, rowTotal };
  });

  const colTotals: Record<string, number> = {};
  for (const col of columns) colTotals[col] = groupTotals[col];
  colTotals.grandTotal = rows.reduce((sum, r) => sum + r.rowTotal, 0);

  return { columns, rows, colTotals };
}

import _ from "lodash";
import { apiJson } from "../../lib/api";
import { FinancialPivot, GroupByOption } from "./FinancialPivot";
import {
  useFinancialMatrix,
  getMerchantLabelForMatrix,
  getVendorLabel,
  MatrixData,
} from "../../hooks/useFinancialMatrix";
import { MonthYearPicker } from "./MonthYearPicker";
import { useAuth } from "../../hooks/useAuth";
import { PlaidTransaction, TransactionSort } from "../../types/transactions";
import { useAccounts } from "../../hooks/useAccounts";
import { useUser } from "../../Context/userContext";
import { AccountSelector } from "../common/AccountSelector";
import { TablePagination } from "../common/TablePagination";
import { PageSizeSelect } from "../common/PageSizeSelect";
import { SankeyFlow, SankeyAggregatedData } from "./SankeyFlow";
import { XactoPivot } from "./XactoPivot";
import { useXactoMatrix, XactoGroupBy, XactoMatrixData } from "../../hooks/useXactoMatrix";
import { MdViewList, MdGridOn, MdAccountTree, MdShowChart, MdPayment } from "react-icons/md";
import { CashFlowChart, CashFlowMonth } from "./CashFlowChart";

import TransactionTable from "../transactions/TransactionTable";
import { useSnapshotTransactions } from "../../hooks/useSnapshotTransactions";
import { PaymentsReport } from "./PaymentsReport";
import { MerchantGroupedView } from "./MerchantGroupedView";
import { useMerchantGrouping } from "../../hooks/useMerchantGrouping";
import { isCheck, isZelle, isTransfer, getFlowDirection, getCheckNumber } from "../../utils/payment-type";
import { EditIcon, CheckIcon, CloseIcon } from "@chakra-ui/icons";
import { EditTransactionModal } from "../transactions/EditTransactionModal";
import { BulkEditModal } from "../transactions/BulkEditModal";
import { AiAnalysisPanel } from "./AiAnalysisPanel";
import { getVendorDisplayLabel } from "../../utils/vendor-label";
import { toUserMessage } from '../../lib/apiError';

// ─── sessionStorage helpers (preserve config across route navigation) ─────────

function readSession<T>(key: string, fallback: T): T {
  try {
    const raw = sessionStorage.getItem(key);
    return raw !== null ? (JSON.parse(raw) as T) : fallback;
  } catch {
    return fallback;
  }
}

function writeSession(key: string, value: unknown): void {
  try {
    sessionStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* unavailable */
  }
}

const formatDateUs = (rawDate?: string | null): string => {
  if (!rawDate) return "";
  const baseDate = rawDate.includes("T") ? rawDate.slice(0, 10) : rawDate;
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(baseDate);
  if (!match) return baseDate;
  const [, year, month, day] = match;
  return `${month}/${day}/${year}`;
};

/**
 * Hook to measure the width of a container element for fixed-size chart rendering
 */
const useContainerWidth = (ref: React.RefObject<HTMLElement>) => {
  const [width, setWidth] = useState<number>(0);

  useEffect(() => {
    if (!ref.current) return;
    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setWidth(entry.contentRect.width);
      }
    });
    observer.observe(ref.current);
    return () => observer.disconnect();
  }, [ref]);

  return width;
};

// Helper to map API response to PlaidTransaction
const mapToPlaidTransaction = (t: any): PlaidTransaction => {
  return {
    ...t,
    id: t.id, // Database UUID - keep as primary ID for navigation
    transactionId: t.transactionId || t.transaction_id, // Plaid transaction ID
    date: t.date,
    datetime: t.datetime || t.date, // Fallback for sort
    amount: Number(t.amount),
    isoCurrencyCode: t.isoCurrencyCode || t.iso_currency_code || "USD",
    merchantName: t.merchantName || t.merchant_name || t.name,
    normalizedMerchant: t.normalizedMerchant || t.normalized_merchant || null,
    normalizationStatus: t.normalizationStatus || t.normalization_status || null,
    normalizationSource: t.normalizationSource || t.normalization_source || null,
    name: t.name,
    originalDescription: t.originalDescription || t.original_description || t.name,
    logoUrl: t.logoUrl || t.logo_url || null,
    paymentChannel: t.paymentChannel || t.payment_channel || null,
    personalFinanceCategoryPrimary:
      t.personalFinanceCategoryPrimary ||
      t.pfc_primary ||
      t.personal_finance_category?.primary ||
      t.category?.[0],
    personalFinanceCategoryDetailed:
      t.personalFinanceCategoryDetailed ||
      t.pfc_detailed ||
      t.personal_finance_category?.detailed ||
      t.category?.[1],
    pending: t.pending || false,
    // Keep original fields just in case but typed properties take precedence
  };
};

export interface TransactionsExplorerProps {
  tenantId?: string;
  initialStartDate?: string;
  initialEndDate?: string;
  forcedDataSourceMode?: "ORIGINAL" | "EDITABLE";
  displayMode?: "pivot" | "table" | "flow" | "chart" | "grouped";
  exportButtonLabel?: string;
  exportButtonProps?: React.ComponentProps<typeof Button>;
  snapshotData?: any[]; // Allow external passing of snapshot data to reuse state from parent
  hideFlowView?: boolean;
  accountIds?: string[];
  onAccountIdsChange?: (ids: string[]) => void;
  // Controlled mode — used by TransactionsV2Page to drive the explorer from external toolbars
  hideControls?: boolean;
  showEditableUtilityBarWhenControlsHidden?: boolean;
  controlledStartDate?: string;
  controlledEndDate?: string;
  controlledViewMode?: "pivot" | "table" | "flow" | "chart" | "payments" | "grouped";
  controlledGroupBy?: GroupByOption;
  controlledSearchQuery?: string;
  controlledSearchMode?: "merchant" | "category";
  filterMode?: "none" | "checks" | "zelle" | "transfer";
  controlledFlowFilter?: "all" | "income" | "expense";
  pivotOrientation?: "vertical" | "horizontal";
  /**
   * When true (only while `controlledViewMode === "table"`), the table view
   * becomes its own vertical scroll container with a sticky column header
   * and an always-visible pager, instead of growing to its full height and
   * relying on the page/document to scroll. Callers set this only for the
   * table view — see `TransactionTable`'s `containedScroll` prop doc for
   * why, and docs/plans/UI_LAYOUT_DENSITY_PLAN.md §H4/§5.4.
   */
  containedScroll?: boolean;
  // External trigger refs — allows parent sidebars to open the AI panel or trigger export
  onAiOpenRef?: React.MutableRefObject<(() => void) | undefined>;
  onExportRef?: React.MutableRefObject<(() => void) | undefined>;
  onDataLoadingChange?: (loading: boolean) => void;
  // External trigger refs — allows parent sidebars (e.g. SnapshotPage) to render the
  // bulk-edit panel outside the scrollable container, so it stays visible on scroll.
  bulkEditOpenRef?: React.MutableRefObject<(() => void) | undefined>;
  clearSelectionRef?: React.MutableRefObject<(() => void) | undefined>;
  onBulkEditPanelChange?: (state: { visible: boolean; count: number }) => void;
  // Controlled edit-status filter — lets parents (e.g. SnapshotPage) render the
  // Status filter UI outside the scrollable container while keeping the filter
  // behaviour owned by the explorer.
  editFilter?: "all" | "edited" | "original";
  onEditFilterChange?: (filter: "all" | "edited" | "original") => void;
}

export const TransactionsExplorer: React.FC<TransactionsExplorerProps> = ({
  tenantId,
  initialStartDate,
  initialEndDate,
  forcedDataSourceMode,
  displayMode = "pivot",
  exportButtonLabel, // Default handled below
  exportButtonProps,
  snapshotData: externalSnapshotData,
  hideFlowView = false,
  accountIds,
  onAccountIdsChange,
  hideControls = false,
  showEditableUtilityBarWhenControlsHidden = false,
  controlledStartDate,
  controlledEndDate,
  controlledViewMode,
  controlledGroupBy,
  controlledSearchQuery,
  controlledSearchMode,
  filterMode,
  controlledFlowFilter,
  pivotOrientation = "horizontal",
  containedScroll = false,
  onAiOpenRef,
  onExportRef,
  onDataLoadingChange,
  bulkEditOpenRef,
  clearSelectionRef,
  onBulkEditPanelChange,
  editFilter: externalEditFilter,
  onEditFilterChange,
}) => {
  const { session } = useAuth();
  // Keep session in a ref so fetch effects always use the latest token
  // without re-running when TOKEN_REFRESHED fires a new session object reference.
  const sessionRef = useRef(session);
  useEffect(() => {
    sessionRef.current = session;
  });
  const hasSession = !!session?.access_token;
  const toast = useToast();
  // Drill-down Drawer State
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { isOpen: isAiPanelOpen, onOpen: onAiPanelOpen, onClose: onAiPanelClose } = useDisclosure();

  // Ref and width tracking for SankeyFlow
  const flowContainerRef = useRef<HTMLDivElement>(null);
  const containerWidth = useContainerWidth(flowContainerRef);

  // Keep external trigger refs up to date every render
  useEffect(() => {
    if (onAiOpenRef) onAiOpenRef.current = onAiPanelOpen;
  });

  // Expose bulk-edit open trigger for parents that render the orange "X selected"
  // panel outside the scrollable container (e.g. SnapshotPage).
  useEffect(() => {
    if (bulkEditOpenRef) bulkEditOpenRef.current = onBulkEditOpen;
  });

  // Expose clear-selection trigger so parents can wire the "X" icon on the panel.
  useEffect(() => {
    if (clearSelectionRef) {
      clearSelectionRef.current = () => {
        setDrillSelectedIds(new Set());
        setDrillSelectAllMode("none");
      };
    }
  });

  const { accounts } = useAccounts();
  const { selectedAccountId, setSelectedAccountId, selectedTenant } = useUser();

  // Namespace per tenant so each tenant has independent config memory
  const sessionKey = `txExplorer:${tenantId ?? ""}`;

  // Use local state if no external control, otherwise use props
  const [localAccountIds, setLocalAccountIds] = useState<string[]>(
    accountIds ?? (selectedAccountId ? [selectedAccountId] : []),
  );

  const activeAccountIds = accountIds ?? localAccountIds;

  const handleAccountIdsChange = useCallback(
    (ids: string[]) => {
      if (onAccountIdsChange) {
        onAccountIdsChange(ids);
      } else {
        setLocalAccountIds(ids);
        // Sync with context for backward compatibility
        if (ids.length === 1) {
          setSelectedAccountId(ids[0]);
        } else {
          setSelectedAccountId("");
        }
      }
    },
    [onAccountIdsChange, setSelectedAccountId],
  );

  // State (account filter can come from props or local/context)
  const [startDate, setStartDate] = useState<string>(
    () => controlledStartDate || initialStartDate || readSession(`${sessionKey}:startDate`, ""),
  );
  const [endDate, setEndDate] = useState<string>(
    () => controlledEndDate || initialEndDate || readSession(`${sessionKey}:endDate`, ""),
  );
  const [viewMode, setViewMode] = useState<
    "pivot" | "table" | "flow" | "chart" | "payments" | "grouped"
  >(() => controlledViewMode || readSession(`${sessionKey}:viewMode`, displayMode));
  const [groupBy, setGroupBy] = useState<GroupByOption>(
    () => controlledGroupBy || readSession(`${sessionKey}:groupBy`, "merchant" as GroupByOption),
  );
  const [data, setData] = useState<PlaidTransaction[]>([]); // Typed as PlaidTransaction
  const [loading, setLoading] = useState<boolean>(false);
  const [drillDownData, setDrillDownData] = useState<PlaidTransaction[]>([]);
  const [drillDownTitle, setDrillDownTitle] = useState<string>("");
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [groupedMode, setGroupedMode] = useState<"consolidated" | "monthly">(() =>
    readSession(`${sessionKey}:groupedMode`, "consolidated" as const),
  );

  // Search & Drill-down State
  const [searchMode, setSearchMode] = useState<"merchant" | "category">("merchant");
  const [drillSort, setDrillSort] = useState<TransactionSort>({
    column: "date",
    direction: "desc",
  });
  const [drillSelectedIds, setDrillSelectedIds] = useState<Set<string>>(new Set());

  const [drillSelectAllMode, setDrillSelectAllMode] = useState<"none" | "displayed" | "all">(
    "none",
  );

  // Notify parent whenever the bulk-edit panel should be visible and how many items
  // are selected. Parent uses this to render a sticky panel above the scrollable area.
  useEffect(() => {
    if (!onBulkEditPanelChange) return;
    const visible =
      drillSelectedIds.size > 0 &&
      (viewMode === "table" || viewMode === "grouped");
    onBulkEditPanelChange({ visible, count: drillSelectedIds.size });
  }, [onBulkEditPanelChange, drillSelectedIds.size, viewMode]);

  // Snapshot Integration
  // Use forced mode if provided, otherwise default to ORIGINAL or internal state
  const [internalMode, setInternalMode] = useState<"ORIGINAL" | "EDITABLE">("ORIGINAL");
  const dataSourceMode = forcedDataSourceMode || internalMode;

  // Edit Modal State
  const {
    isOpen: isEditModalOpen,
    onOpen: onEditModalOpen,
    onClose: onEditModalClose,
  } = useDisclosure();
  const {
    isOpen: isBulkEditOpen,
    onOpen: onBulkEditOpen,
    onClose: onBulkEditClose,
  } = useDisclosure();
  const [editingTransaction, setEditingTransaction] = useState<PlaidTransaction | null>(null);

  // Edit filter & flash animation state
  // When parent provides editFilter/onEditFilterChange the filter is controlled
  // from outside (so the Status filter UI can live in the parent's sticky bar).
  const [internalEditFilter, setInternalEditFilter] = useState<"all" | "edited" | "original">("all");
  const editFilter = externalEditFilter ?? internalEditFilter;
  const setEditFilter = (next: "all" | "edited" | "original") => {
    if (onEditFilterChange) onEditFilterChange(next);
    else setInternalEditFilter(next);
  };
  const [recentlyEditedId, setRecentlyEditedId] = useState<string | null>(null);

  // Holds whichever TransactionRow's inline-edit commit function is
  // currently registered (see TransactionRow's onEditingActiveChange) — the
  // pagination controls call this before navigating away, so an in-progress
  // edit is saved instead of silently lost when the row unmounts.
  const pendingRowCommitRef = useRef<(() => Promise<boolean>) | null>(null);
  const flushPendingRowEdit = useCallback(async (): Promise<boolean> => {
    if (!pendingRowCommitRef.current) return true;
    const ok = await pendingRowCommitRef.current();
    if (!ok) {
      toast({
        title: "Finish or cancel the current edit before changing page",
        status: "warning",
        duration: 3000,
      });
    }
    return ok;
  }, [toast]);

  // Flow direction filter
  const [flowFilter, setFlowFilter] = useState<"all" | "income" | "expense">("all");

  // Bumped after every edit that reached the database. The aggregate views
  // read from SQL, so an edit applied optimistically to effectiveData (and
  // persisted server-side) would otherwise leave them showing the pre-edit
  // grouping until an unrelated filter change happened to refetch. This is
  // reachable: clicking a pivot cell opens a drill-down that can rename a
  // vendor or recategorize, which is exactly what the pivot groups by.
  // Identifies the newest fetchAllOriginalData run so older ones can stop.
  const originalFetchRunIdRef = useRef(0);

  const [aggregateRevision, setAggregateRevision] = useState(0);
  const invalidateServerAggregates = useCallback(
    () => setAggregateRevision((r) => r + 1),
    [],
  );

  // Server-side aggregations — each populated while its view is active,
  // replacing the client-side reduce over the full while-loop fetch.
  //
  // The loading flags start true when mounting directly into their view (a
  // shareable URL can deep-link straight to it) because the render decides
  // spinner-vs-empty-state before the fetch effect has had a chance to set
  // them: seeded false, a data-rich page flashes "No transactions found"
  // for a frame. Each effect sets the flag back to false on the path where
  // it doesn't fetch, so this can't strand a spinner.
  const [serverPivotData, setServerPivotData] = useState<XactoMatrixData | null>(null);
  const [serverPivotLoading, setServerPivotLoading] = useState(() => viewMode === "pivot");
  const [serverChartData, setServerChartData] = useState<{ months: CashFlowMonth[] } | null>(null);
  const [serverChartLoading, setServerChartLoading] = useState(() => viewMode === "chart");
  const [serverFlowData, setServerFlowData] = useState<SankeyAggregatedData | null>(null);
  const [serverFlowLoading, setServerFlowLoading] = useState(() => viewMode === "flow");
  const [drillDownLoading, setDrillDownLoading] = useState(false);

  const {
    loading: snapshotLoading,
    snapshotData: internalSnapshotData,
    dataVersion,
    availableCategories: dbCategories,
    initSnapshot,
    resetSnapshot,
    fetchSnapshotData,
    fetchCategories,
    updateTransaction,
    revertTransaction,
    bulkUpdateTransactions,
    // Paginated table-view API — see fetchSnapshotPage effect below. Kept
    // separate from snapshotData/mappedSnapshotData (still fetched in full
    // for the pivot/flow/balance/grouped views — see docs/plans/
    // TRANSACTIONS_PAGINATION_PLAN.md, Phase 1.4, for extending those too).
    pageResult: snapshotPageResult,
    pageLoading: snapshotPageLoading,
    fetchSnapshotPage,
  } = useSnapshotTransactions(tenantId, activeAccountIds);

  // Use external data if provided, otherwise internal
  const snapshotData = externalSnapshotData || internalSnapshotData;

  const isDataLoading =
    (dataSourceMode === "EDITABLE" ? snapshotLoading : loading) ||
    serverPivotLoading ||
    serverChartLoading ||
    serverFlowLoading;

  useEffect(() => {
    onDataLoadingChange?.(isDataLoading);
  }, [isDataLoading, onDataLoadingChange]);

  const handleBulkSave = async (updates: {
    pfc_primary?: string;
    pfc_detailed?: string;
    merchant?: string;
    normalized_merchant?: string;
  }) => {
    if (drillSelectedIds.size === 0) return;

    await bulkUpdateTransactions(Array.from(drillSelectedIds), updates);
    invalidateServerAggregates();

    // Clear selection after update
    setDrillSelectedIds(new Set());
    setDrillSelectAllMode("none");
    fetchCategories(); // Refresh categories in case a custom one was created
  };

  // Maps one raw ClonedTransaction row (as returned by GET
  // /api/transactions/snapshot, in either its full-array or single-page
  // shape — the per-row fields are identical) to the PlaidTransaction shape
  // the rest of the explorer renders. Shared by mappedSnapshotData (full
  // array, aggregate views) and mappedSnapshotPageRows (one page, table
  // view) so the two can never drift apart.
  const mapClonedRow = React.useCallback(
    (t: any): PlaidTransaction => {
      // Normalize date format
      let dateStr = t.date;
      if (dateStr instanceof Date) {
        dateStr = dateStr.toISOString().split("T")[0];
      } else if (typeof dateStr === "string" && dateStr.includes("T")) {
        dateStr = dateStr.split("T")[0];
      }

      // CRITICAL ID MAPPING:
      // - t.id = UUID of the ClonedTransaction record (snapshot record) - USE FOR UPDATES
      // - t.originalPlaidTransactionId/t.original_plaid_transaction_id = UUID of original transaction - USE FOR NAVIGATION
      const snapshotId = t.id; // ClonedTransaction UUID - for bulk updates
      const originalTransactionId = t.originalPlaidTransactionId || t.original_plaid_transaction_id; // Original transaction UUID - for navigation
      const snapshotAccountId = t.account_id || t.plaid_account_id || null;
      const resolvedAccountName =
        t.account_name ||
        (snapshotAccountId ? accounts.find((a) => a.id === snapshotAccountId)?.name : null) ||
        "Unknown Account";

      return {
        ...t,
        id: snapshotId, // ✅ ClonedTransaction ID for updates (preserves original t.id)
        originalPlaidTransactionId: originalTransactionId, // ✅ Original transaction UUID for navigation detail
        transactionId: originalTransactionId || t.id, // Plaid or original transaction ID
        amount: Number(t.amount),
        date: dateStr,
        name: t.name || "",
        merchantName: t.merchant || t.merchantName,
        normalizedMerchant: t.normalizedMerchant || t.normalized_merchant || null,
        normalizationStatus: t.normalizationStatus || t.normalization_status || null,
        normalizationSource: t.normalizationSource || t.normalization_source || null,
        personalFinanceCategoryPrimary: t.pfcPrimary || t.pfc_primary,
        personalFinanceCategoryDetailed: t.pfcDetailed || t.pfc_detailed,
        isModified: t.isModified || t.is_modified,
        pending: false,
        isoCurrencyCode: "USD",
        checkNumber: t.checkNumber || t.check_number || null,
        paymentChannel: t.paymentChannel || null,
        plaidAccount: {
          id: snapshotAccountId || "snapshot-account",
          accountId: snapshotAccountId || "snapshot-account",
          name: resolvedAccountName,
        },
      } as PlaidTransaction;
    },
    [accounts],
  );

  // Map snapshotData to PlaidTransaction[] format (reactive via useMemo).
  // Full array — feeds the pivot/flow/balance/grouped EDITABLE views, which
  // still aggregate client-side (see fetchSnapshotData's docstring in
  // useSnapshotTransactions.ts). The table view does NOT use this — see
  // mappedSnapshotPageRows.
  const mappedSnapshotData = React.useMemo(() => {
    if (dataSourceMode !== "EDITABLE" || !snapshotData) return [];

    const rawSnapshotData = snapshotData as any;

    // Check if we received HTML instead of JSON
    if (typeof rawSnapshotData === "string" && rawSnapshotData.trim().startsWith("<")) {
      console.error("CRITICAL API ERROR: Received HTML content instead of JSON.");
      return [];
    }

    // Safely convert to array
    let safeArray: any[] = [];
    if (Array.isArray(snapshotData)) {
      safeArray = snapshotData;
    } else if (snapshotData && typeof snapshotData === "object" && "length" in snapshotData) {
      safeArray = Array.from(snapshotData as any);
    }

    return safeArray.map(mapClonedRow);
  }, [snapshotData, dataSourceMode, dataVersion, mapClonedRow]);

  // Server-side page size for the Scenario Editor's table view, user
  // adjustable via PageSizeSelect. 100 is a sane default: small enough to
  // virtualize trivially without a virtualization library, large enough
  // that paging feels infrequent for a normal browsing session.
  const [snapshotPageSize, setSnapshotPageSize] = useState(100);
  const [snapshotPage, setSnapshotPage] = useState(1);

  // Identifies the *result set* — everything except which page of it we're
  // looking at. Used below to reset to page 1 on any filter change without
  // the two-effect race that would otherwise cause it: a separate "reset
  // page" effect and a separate "fetch page" effect (keyed on snapshotPage)
  // fire in the same commit, but setSnapshotPage(1) doesn't take effect
  // until the NEXT render — so the fetch effect would still read the stale
  // page number and fire one wrong-offset request before self-correcting.
  // Merging reset and fetch into one effect (below) makes the reset visible
  // to the very fetch it's supposed to affect.
  const snapshotFilterSignature = JSON.stringify([
    startDate,
    endDate,
    activeAccountIds,
    searchQuery,
    searchMode,
    filterMode,
    flowFilter,
    editFilter,
    snapshotPageSize,
  ]);
  const prevSnapshotFilterSignatureRef = useRef(snapshotFilterSignature);
  // De-dupes the redundant re-run this effect otherwise causes on a filter
  // change: setSnapshotPage(1) below still triggers a second commit (any
  // setState does), and on that second run filtersChanged has already gone
  // false — without this guard it would fire the exact same fetch a second
  // time. Keyed on the (signature, offset) actually sent, not on component
  // state, so it stays correct across renders regardless of *why* the
  // effect re-ran.
  const lastFetchKeyRef = useRef<string | null>(null);

  // Fetch exactly one page for the table view. Re-runs on dataVersion so any
  // successful edit (single, inline, bulk, revert — all bump dataVersion in
  // useSnapshotTransactions) refreshes the currently-visible page from the
  // server, rather than trusting a client-side array mutation that can't see
  // rows outside this page (the bug this whole pagination effort exists to
  // fix — see docs/plans/TRANSACTIONS_PAGINATION_PLAN.md, R2-C). dataVersion
  // deliberately bypasses the de-dupe guard below (via forceKey), since an
  // edit can leave the signature+offset identical to the last fetch yet
  // still needs a real refetch to pick up the change.
  const lastFetchDataVersionRef = useRef(dataVersion);
  useEffect(() => {
    if (dataSourceMode !== "EDITABLE" || viewMode !== "table") return;
    if (!tenantId || !hasSession) return;

    const filtersChanged = prevSnapshotFilterSignatureRef.current !== snapshotFilterSignature;
    prevSnapshotFilterSignatureRef.current = snapshotFilterSignature;

    const pageToFetch = filtersChanged ? 1 : snapshotPage;
    if (filtersChanged && snapshotPage !== 1) {
      // Only affects the page-number UI and future clicks — the fetch below
      // already uses pageToFetch=1 directly for THIS call.
      setSnapshotPage(1);
    }

    const offset = (pageToFetch - 1) * snapshotPageSize;
    const dataVersionChanged = lastFetchDataVersionRef.current !== dataVersion;
    lastFetchDataVersionRef.current = dataVersion;
    const fetchKey = `${snapshotFilterSignature}|${offset}`;
    if (fetchKey === lastFetchKeyRef.current && !dataVersionChanged) {
      return; // identical to the last real fetch — the setSnapshotPage(1) re-run above
    }
    lastFetchKeyRef.current = fetchKey;

    fetchSnapshotPage({
      startDate,
      endDate,
      search: searchQuery,
      searchMode,
      filterMode,
      flowFilter,
      editFilter,
      sortBy: "flow_alpha",
      limit: snapshotPageSize,
      offset,
    });
    // fetchSnapshotPage is stable (useCallback in the hook) but intentionally
    // omitted: including it would be redundant with its own deps and adds
    // noise. activeAccountIds is baked into the hook instance itself (passed
    // as its second constructor arg) and folded into snapshotFilterSignature
    // here, not read directly.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    dataSourceMode,
    viewMode,
    tenantId,
    hasSession,
    snapshotFilterSignature,
    snapshotPage,
    dataVersion,
  ]);

  const mappedSnapshotPageRows = React.useMemo(
    () => snapshotPageResult.transactions.map(mapClonedRow),
    [snapshotPageResult, mapClonedRow],
  );

  // ── ORIGINAL mode's table pagination — mirrors the snapshot pagination
  // above, against GET /api/plaid-transactions instead of the snapshot
  // endpoint. This is the fix for the reported /transactions slowness: the
  // while-loop below (fetchAllOriginalData) is what used to run for every
  // view, table included, transferring the entire filtered dataset (all
  // 50k+ rows at 5000/request) before a single row could render.
  //
  // Sort: date desc, NOT flow_alpha (income-first-alphabetical, which the
  // OTHER views/table used to get via sortedTableData's fixed client sort).
  // Replicating flow_alpha server-side would need a raw-SQL rewrite of
  // getTransactions() (Prisma's typed orderBy sorts NULL merchantName rows
  // — checks, transfers — differently than the client's `merchantName ||
  // name` fallback did), which is a lot of duplicated, riskier query logic
  // for a sort order nothing in the UI actually lets a user choose anyway
  // (the column-header sort controls are wired to state that was already
  // disconnected from this table's rendering before this change — a
  // pre-existing bug, not something introduced here). date-desc is a
  // reasonable, disclosed default for what is fundamentally a transaction
  // ledger. Non-table ORIGINAL views (pivot/flow/balance/grouped) keep
  // using fetchAllOriginalData + the client-side flow_alpha sort, so they
  // are completely unaffected by this.
  const [originalPageSize, setOriginalPageSize] = useState(100);
  const [originalPage, setOriginalPage] = useState(1);
  const [originalPageResult, setOriginalPageResult] = useState<{
    transactions: any[];
    total: number;
    hasMore: boolean;
  }>({ transactions: [], total: 0, hasMore: false });
  const [originalPageLoading, setOriginalPageLoading] = useState(false);

  const fetchOriginalPage = useCallback(
    async (opts: { offset: number }) => {
      const s = sessionRef.current;
      if (!tenantId || !s) return;

      setOriginalPageLoading(true);
      try {
        const params = new URLSearchParams();
        if (startDate) params.append("startDate", startDate);
        if (endDate) params.append("endDate", endDate);
        if (activeAccountIds.length > 0) params.append("accountIds", activeAccountIds.join(","));
        if (searchQuery.trim()) {
          params.append("search", searchQuery.trim());
          params.append("searchMode", searchMode);
        }
        if (flowFilter !== "all") {
          params.append("transactionClass", flowFilter === "income" ? "INCOME" : "EXPENSE");
        }
        params.append("includePending", "false");
        params.append("sortBy", "date");
        params.append("sortOrder", "desc");
        params.append("limit", String(originalPageSize));
        params.append("offset", String(opts.offset));

        const result = await apiJson<{ transactions: any[]; total: number; hasMore: boolean }>(
          `/api/plaid-transactions?${params.toString()}`,
          { headers: { "x-tenant-id": tenantId } },
          s.access_token,
        );
        setOriginalPageResult(result);
        return result;
      } catch (error) {
        console.error("Error fetching transactions page:", error);
        toast({ title: "Error loading transactions", status: "error", duration: 4000 });
      } finally {
        setOriginalPageLoading(false);
      }
    },
    [tenantId, startDate, endDate, activeAccountIds, searchQuery, searchMode, flowFilter, originalPageSize, toast],
  );

  const originalFilterSignature = JSON.stringify([
    startDate,
    endDate,
    activeAccountIds,
    searchQuery,
    searchMode,
    flowFilter,
    originalPageSize,
  ]);
  const prevOriginalFilterSignatureRef = useRef(originalFilterSignature);
  const lastOriginalFetchKeyRef = useRef<string | null>(null);

  const isOriginalTableView = dataSourceMode === "ORIGINAL" && viewMode === "table";

  useEffect(() => {
    if (!isOriginalTableView) return;
    if (!tenantId || !hasSession) return;

    const filtersChanged = prevOriginalFilterSignatureRef.current !== originalFilterSignature;
    prevOriginalFilterSignatureRef.current = originalFilterSignature;

    const pageToFetch = filtersChanged ? 1 : originalPage;
    if (filtersChanged && originalPage !== 1) {
      setOriginalPage(1);
    }

    const offset = (pageToFetch - 1) * originalPageSize;
    const fetchKey = `${originalFilterSignature}|${offset}`;
    if (fetchKey === lastOriginalFetchKeyRef.current) return;
    lastOriginalFetchKeyRef.current = fetchKey;

    fetchOriginalPage({ offset });
    // fetchOriginalPage is stable-ish (recreated when its own deps change,
    // all of which are already in this effect's deps via
    // originalFilterSignature) but intentionally omitted to avoid the
    // two-effect stale-page race documented on the snapshot version above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOriginalTableView, tenantId, hasSession, originalFilterSignature, originalPage]);

  const mappedOriginalPageRows = React.useMemo(
    () => originalPageResult.transactions.map(mapToPlaidTransaction),
    [originalPageResult],
  );

  // Vendor overrides: PlaidTransaction ID → normalized vendor (persisted after PATCH /vendor)
  const [vendorOverrides, setVendorOverrides] = useState<Record<string, string | null>>({});

  // Effective data: use mappedSnapshotData in EDITABLE mode, data in ORIGINAL mode
  // Apply any pending vendor overrides on top so edits are reflected immediately.
  const effectiveData = React.useMemo(() => {
    const base = dataSourceMode === "EDITABLE" ? mappedSnapshotData : data;
    if (Object.keys(vendorOverrides).length === 0) return base;
    return base.map((t) => {
      const plaidId =
        dataSourceMode === "EDITABLE"
          ? t.originalPlaidTransactionId || t.transactionId || t.id
          : t.id;
      if (plaidId && plaidId in vendorOverrides) {
        return { ...t, normalizedMerchant: vendorOverrides[plaidId] };
      }
      return t;
    });
  }, [dataSourceMode, mappedSnapshotData, data, vendorOverrides]);

  // Filtering Logic (applies date range and search filters - account filtering is server-side)
  // filteredDataBeforeFlow: all filters except flow direction — used for Balance Mensual sheet
  const filteredDataBeforeFlow = React.useMemo(() => {
    let result = effectiveData;

    // Note: Account filtering is already done server-side (via API accountIds parameter)
    // - ORIGINAL mode: /api/plaid-transactions filters by accountIds
    // - EDITABLE mode: /api/transactions/snapshot filters by accountIds in getSnapshotTransactions()
    // So we don't need to filter here by account

    // Apply date range filter.
    // Normalize to YYYY-MM-DD before comparing: Prisma findMany serializes
    // DateTime @db.Date as "2025-12-31T00:00:00.000Z", so a plain string
    // comparison would incorrectly exclude transactions on the endDate itself.
    // $queryRaw (snapshot) already returns DATE as "YYYY-MM-DD", so substring
    // is a no-op for those — safe for both modes.
    if (startDate || endDate) {
      result = result.filter((t) => {
        const txDate = t.date ? t.date.substring(0, 10) : "";
        if (startDate && txDate < startDate) return false;
        if (endDate && txDate > endDate) return false;
        return true;
      });
    }

    // Apply search filter
    if (searchQuery.trim()) {
      const lowerQuery = searchQuery.toLowerCase();
      result = result.filter((t) => {
        if (searchMode === "merchant") {
          const merchant = (t.merchantName || "").toLowerCase();
          const name = (t.name || "").toLowerCase();
          return merchant.includes(lowerQuery) || name.includes(lowerQuery);
        } else {
          // Category Mode
          const primary = (t.personalFinanceCategoryPrimary || "").toLowerCase();
          const detailed = (t.personalFinanceCategoryDetailed || "").toLowerCase();
          return primary.includes(lowerQuery) || detailed.includes(lowerQuery);
        }
      });
    }

    // Apply edit status filter (only relevant in EDITABLE mode)
    if (dataSourceMode === "EDITABLE" && editFilter !== "all") {
      if (editFilter === "edited") {
        result = result.filter((t) => t.isModified);
      } else if (editFilter === "original") {
        result = result.filter((t) => !t.isModified);
      }
    }

    // Apply external filterMode (checks / zelle / transfer) from V2 toolbar
    if (filterMode === "checks") {
      result = result.filter(isCheck);
    } else if (filterMode === "zelle") {
      result = result.filter(isZelle);
    } else if (filterMode === "transfer") {
      result = result.filter(isTransfer);
    }

    return result;
  }, [
    effectiveData,
    searchQuery,
    searchMode,
    startDate,
    endDate,
    activeAccountIds,
    dataSourceMode,
    accounts,
    editFilter,
    filterMode,
  ]);

  // filteredData: applies flow direction filter on top of filteredDataBeforeFlow
  const filteredData = React.useMemo(() => {
    if (flowFilter === "income")
      return filteredDataBeforeFlow.filter((t) => getFlowDirection(t) === "INCOME");
    if (flowFilter === "expense")
      return filteredDataBeforeFlow.filter((t) => getFlowDirection(t) === "EXPENSE");
    return filteredDataBeforeFlow;
  }, [filteredDataBeforeFlow, flowFilter]);

  // Table view sort: INCOME (amount < 0) first alphabetically, then EXPENSE alphabetically
  const sortedTableData = React.useMemo(() => {
    return [...filteredData].sort((a, b) => {
      const aIsIncome = a.amount < 0;
      const bIsIncome = b.amount < 0;
      if (aIsIncome !== bIsIncome) return aIsIncome ? -1 : 1;
      const aName = (a.merchantName || a.name || "").toLowerCase();
      const bName = (b.merchantName || b.name || "").toLowerCase();
      return aName.localeCompare(bName);
    });
  }, [filteredData]);

  // Table view's data source: paginated (one page from the server) in
  // EDITABLE mode, its own dedicated paginated fetch for ORIGINAL mode
  // (isOriginalTableView, declared above). Non-table views (pivot/flow/
  // balance/grouped, either data source) keep using the shared
  // filteredData/sortedTableData pipeline — that path is a separate,
  // later phase of this plan; see docs/plans/TRANSACTIONS_PAGINATION_PLAN.md.
  // Declared here (rather than just above the render) because
  // handleSaveVendor, below, needs it.
  const isSnapshotTableView = dataSourceMode === "EDITABLE" && viewMode === "table";
  const isPaginatedTableView = isSnapshotTableView || isOriginalTableView;
  const tableRows = isSnapshotTableView
    ? mappedSnapshotPageRows
    : isOriginalTableView
      ? mappedOriginalPageRows
      : sortedTableData;
  const tableTotalCount = isSnapshotTableView
    ? snapshotPageResult.total
    : isOriginalTableView
      ? originalPageResult.total
      : sortedTableData.length;
  const snapshotTotalPages = Math.max(1, Math.ceil(snapshotPageResult.total / snapshotPageSize));
  const originalTotalPages = Math.max(1, Math.ceil(originalPageResult.total / originalPageSize));

  const matrixData = useFinancialMatrix(filteredData, groupBy);

  // XactoView: derive column grouping from current groupBy.
  // Fixed two pre-existing bugs while extending server-pivot support below
  // (found, not introduced, by this change — see the isServerPivotMode
  // comment): xactoGroupBy only recognized "vendor", never "vendor_year"
  // (so "Vendor by Year" silently fell into the merchant branch), and
  // xactoPeriod only recognized "subcategory_year" as a year period, so
  // "Vendor by Year"/"Merchant by Year"/"Category by Year" all rendered
  // monthly columns instead of yearly ones.
  const xactoGroupBy: XactoGroupBy =
    groupBy === "category" || groupBy === "category_year"
      ? "category"
      : groupBy === "subcategory" || groupBy === "subcategory_year"
        ? "subcategory"
        : groupBy === "vendor" || groupBy === "vendor_year"
          ? "vendor"
          : "merchant";
  const xactoPeriod: 'month' | 'year' = groupBy.endsWith("_year") ? "year" : "month";
  const xactoMatrixData = useXactoMatrix(filteredData, xactoGroupBy, xactoPeriod);
  const matrixMonthlySummary = useFinancialMatrix(filteredData, "month");
  const matrixYearlySummary = useFinancialMatrix(filteredData, "year");

  // XactoPivot is used for monthly groupings (columns = merchants/vendors/categories, rows = months)
  const useXactoLayout = groupBy === "merchant" || groupBy === "category" || groupBy === "vendor" || groupBy === "subcategory" || groupBy === "subcategory_year";

  // Grouped view: one canonical row per (month × merchant × direction × semanticType × isSelf)
  const merchantGroupedRows = useMerchantGrouping(filteredData);

  // Monthly income/expenses for the Cash Flow chart (computed client-side)
  const monthlyChartData = React.useMemo((): CashFlowMonth[] => {
    const byMonth: Record<string, { income: number; expenses: number }> = {};
    for (const t of filteredData) {
      const m = t.date.slice(0, 7); // YYYY-MM
      if (!byMonth[m]) byMonth[m] = { income: 0, expenses: 0 };
      if (t.amount < 0) byMonth[m].income += Math.abs(t.amount);
      else byMonth[m].expenses += t.amount;
    }
    return Object.entries(byMonth)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([month, v]) => ({ month, ...v }));
  }, [filteredData]);

  // Edit counts for filter badges (computed from effectiveData, not filteredData, to avoid circular dependency)
  const { editedCount, originalCount } = React.useMemo(() => {
    if (dataSourceMode !== "EDITABLE") return { editedCount: 0, originalCount: 0 };
    const edited = effectiveData.filter((t) => t.isModified).length;
    return { editedCount: edited, originalCount: effectiveData.length - edited };
  }, [effectiveData, dataSourceMode]);

  // Sync controlled props into internal state when they change from outside
  useEffect(() => {
    if (controlledStartDate) setStartDate(controlledStartDate);
  }, [controlledStartDate]);
  useEffect(() => {
    if (controlledEndDate) setEndDate(controlledEndDate);
  }, [controlledEndDate]);
  useEffect(() => {
    if (controlledViewMode) setViewMode(controlledViewMode);
  }, [controlledViewMode]);
  useEffect(() => {
    if (controlledGroupBy) setGroupBy(controlledGroupBy);
  }, [controlledGroupBy]);
  useEffect(() => {
    if (controlledSearchQuery !== undefined) setSearchQuery(controlledSearchQuery);
  }, [controlledSearchQuery]);
  useEffect(() => {
    if (controlledSearchMode) setSearchMode(controlledSearchMode);
  }, [controlledSearchMode]);
  useEffect(() => {
    if (controlledFlowFilter) setFlowFilter(controlledFlowFilter);
  }, [controlledFlowFilter]);

  // Initialize dates to current month if not provided
  useEffect(() => {
    if (!startDate || !endDate) {
      const now = new Date();
      const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
      const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);

      if (!startDate) setStartDate(firstDay.toISOString().split("T")[0]);
      if (!endDate) setEndDate(lastDay.toISOString().split("T")[0]);
    }
  }, [startDate, endDate]);

  // Persist configuration to sessionStorage so it survives route navigation
  useEffect(() => {
    writeSession(`${sessionKey}:viewMode`, viewMode);
    writeSession(`${sessionKey}:groupBy`, groupBy);
    writeSession(`${sessionKey}:groupedMode`, groupedMode);
    if (startDate) writeSession(`${sessionKey}:startDate`, startDate);
    if (endDate) writeSession(`${sessionKey}:endDate`, endDate);
  }, [sessionKey, viewMode, groupBy, groupedMode, startDate, endDate]);

  // Fetch categories from DB when in EDITABLE mode
  useEffect(() => {
    if (dataSourceMode === "EDITABLE" && tenantId && hasSession) {
      fetchCategories();
    }
  }, [dataSourceMode, tenantId, hasSession, fetchCategories]);

  // Reusable full-dataset fetch (while-loop). Used by the useEffect and on-demand (export, drill-down).
  const fetchAllOriginalData = useCallback(async () => {
    const s = sessionRef.current;
    if (!tenantId || !s) return;

    // Supersede any loop still in flight — same rationale as
    // fetchSnapshotData's guard: this walks the whole date range page by
    // page, and re-firing it (dates settling on mount, accounts changing)
    // otherwise leaves several complete loops racing.
    const runId = ++originalFetchRunIdRef.current;
    const superseded = () => originalFetchRunIdRef.current !== runId;

    setLoading(true);
    try {
      const PAGE_SIZE = 5000;
      let allTransactions: PlaidTransaction[] = [];
      let offset = 0;
      let hasMore = true;

      while (hasMore) {
        if (superseded()) return;
        const params = new URLSearchParams();
        if (startDate) params.append("startDate", startDate);
        if (endDate) params.append("endDate", endDate);
        params.append("limit", PAGE_SIZE.toString());
        params.append("offset", offset.toString());
        params.append("includePending", "false");
        if (activeAccountIds.length > 0) {
          params.append("accountIds", activeAccountIds.join(","));
        }

        const result = await apiJson<{ transactions: any[]; total: number; hasMore: boolean }>(
          `/api/plaid-transactions?${params.toString()}`,
          { headers: { "x-tenant-id": tenantId } },
          s.access_token,
        );

        const mapped = (result.transactions || []).map(mapToPlaidTransaction);
        allTransactions = allTransactions.concat(mapped);
        hasMore = result.hasMore;
        offset += (result.transactions || []).length;
      }

      if (superseded()) return;
      setData(allTransactions);
      setDrillDownData(allTransactions);
    } catch (error) {
      if (superseded()) return;
      console.error("Error fetching data:", error);
      toast({
        title: "We couldn't load your transactions",
        description: toUserMessage(error, "Please try again in a moment."),
        status: "error",
        duration: 5000,
      });
      setData([]);
    } finally {
      if (!superseded()) setLoading(false);
    }
  }, [tenantId, startDate, endDate, activeAccountIds, toast]);

  // Whether the XactoPivot (rows=months) layout is active with a
  // server-aggregated backend (ORIGINAL's getPivotData, or EDITABLE's
  // getSnapshotPivotData for the Scenario Editor). In this mode a fast
  // /pivot call shows results immediately instead of the while-loop
  // (fetchAllOriginalData/fetchSnapshotData) running to build the same
  // matrix client-side.
  //
  // Covers merchant/vendor/subcategory (all four period variants — month
  // and year) but NOT category: both backends group "category" by the
  // primary PFC alone (one column per primary category), while the
  // client's useXactoMatrix groups it by composite "primary::detailed"
  // keys nested under categoryGroups[primary] for a collapsible two-level
  // column display. Extending the backend to produce that same
  // composite/grouped shape is real, separate work — not done here — so
  // "Category by Month/Year" still falls back to the full client-side
  // fetch+aggregate.
  // The aggregate endpoints implement date range, accounts, and flow
  // direction — but NOT the search box, the checks/Zelle/transfer filter,
  // or the Scenario Editor's edited/original filter, all of which
  // filteredDataBeforeFlow applies client-side. Using SQL while one of
  // those is active would silently show *more* data than the user asked
  // for (verified: searching "Starbucks" in Flow view still rendered every
  // CHECK #… node), so fall back to the client aggregation whenever the
  // two wouldn't agree. That fallback is always available here: unlike the
  // table views, these keep running the full fetch below. The editFilter
  // term mirrors filteredDataBeforeFlow, which only applies it in EDITABLE
  // mode — gating on it in ORIGINAL mode would disable the fast path for a
  // filter that isn't doing anything.
  const serverAggregatesMatchFilters =
    !searchQuery.trim() &&
    (!filterMode || filterMode === "none") &&
    (dataSourceMode !== "EDITABLE" || editFilter === "all");

  const isServerPivotMode =
    viewMode === "pivot" && xactoGroupBy !== "category" && serverAggregatesMatchFilters;
  // "chart" (balance) and "flow" (Sankey) have no groupBy restriction,
  // unlike pivot's category carve-out above. Same caveat as
  // isServerPivotMode: the full client fetch below still runs in parallel
  // (export and drill-down read the raw transactions directly), so this
  // only replaces the *display* data with the faster SQL aggregation while
  // it's available, falling back to the client-computed value
  // (monthlyChartData / raw `data` passed to SankeyFlow) if it isn't.
  const isServerChartMode = viewMode === "chart" && serverAggregatesMatchFilters;
  const isServerFlowMode = viewMode === "flow" && serverAggregatesMatchFilters;

  // Fetch Data Effect - Enhanced for Snapshot Mode
  useEffect(() => {
    if (dataSourceMode === "ORIGINAL") {
      if (viewMode === "table") return; // handled by fetchOriginalPage's own effect above
      // Non-table ORIGINAL views (pivot/flow/chart/payments/grouped) still
      // fetch the full (date-range-scoped) dataset client-side: it backs
      // export, drill-down, and the "category" pivot/grouped views that
      // aren't server-aggregated. serverPivotData/serverChartData/
      // serverFlowData (below) are an additive fast path used for display
      // only — this fetch keeps running alongside them so those other
      // features keep working.
      if (startDate && endDate && hasSession && tenantId) {
        fetchAllOriginalData();
      }
    } else if (viewMode !== "table") {
      // EDITABLE mode, non-table view — same rationale as above. The table
      // view is handled by its own paginated effect (fetchSnapshotPage
      // above) and must NOT also trigger this full fetch — that would
      // defeat the point of paginating it.
      if (tenantId && hasSession) {
        fetchSnapshotData();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    startDate,
    endDate,
    hasSession,
    tenantId,
    dataSourceMode,
    viewMode,
    fetchSnapshotData,
    activeAccountIds,
  ]); // fetchAllOriginalData omitted — it's derived from these same deps

  // Server pivot fetch — runs whenever the key filters change while in server-pivot mode
  useEffect(() => {
    if (!isServerPivotMode || !startDate || !endDate || !hasSession || !tenantId) {
      setServerPivotData(null);
      setServerPivotLoading(false);
      return;
    }

    // `cancelled` discards a response whose filters are already stale:
    // these requests are fired on every filter change and can resolve out
    // of order, so without it a slow early request can land after a fast
    // later one and repaint the view with the wrong filters' numbers.
    let cancelled = false;

    const fetchServerPivot = async () => {
      const s = sessionRef.current;
      if (!s) return;

      setServerPivotLoading(true);
      try {
        const params = new URLSearchParams();
        params.set("startDate", startDate);
        params.set("endDate", endDate);
        if (activeAccountIds.length > 0) params.set("accountIds", activeAccountIds.join(","));
        params.set("groupBy", xactoGroupBy);
        params.set("period", xactoPeriod);
        if (flowFilter !== "all") params.set("flowFilter", flowFilter);

        const endpoint =
          dataSourceMode === "EDITABLE"
            ? "/api/transactions/snapshot/pivot"
            : "/api/plaid-transactions/pivot";

        const result = await apiJson<XactoMatrixData>(
          `${endpoint}?${params}`,
          { headers: { "x-tenant-id": tenantId } },
          s.access_token,
        );
        if (cancelled) return;
        // Guard against a malformed/unexpected response shape (e.g. a mock
        // or proxy returning a generic {transactions,total,hasMore} body) —
        // XactoPivot indexes straight into .rows with no null-checking, so
        // an unvalidated value here would crash the whole view instead of
        // gracefully falling back to xactoMatrixData.
        setServerPivotData(result && Array.isArray(result.rows) ? result : null);
      } catch (err) {
        if (cancelled) return;
        console.error("Server pivot fetch failed, falling back to client-side:", err);
        setServerPivotData(null);
      } finally {
        if (!cancelled) setServerPivotLoading(false);
      }
    };

    fetchServerPivot();
    return () => {
      cancelled = true;
    };
  }, [
    aggregateRevision,
    isServerPivotMode,
    startDate,
    endDate,
    activeAccountIds,
    xactoGroupBy,
    xactoPeriod,
    flowFilter,
    dataSourceMode,
    tenantId,
    hasSession,
  ]);

  // Server monthly cash-flow fetch — runs whenever the key filters change
  // while in "chart" (balance) view. Replaces monthlyChartData's client
  // aggregation over the full while-loop-fetched dataset with a single
  // grouped-by-month SQL query.
  useEffect(() => {
    if (!isServerChartMode || !startDate || !endDate || !hasSession || !tenantId) {
      setServerChartData(null);
      setServerChartLoading(false);
      return;
    }

    let cancelled = false;

    const fetchServerChart = async () => {
      const s = sessionRef.current;
      if (!s) return;

      setServerChartLoading(true);
      try {
        const params = new URLSearchParams();
        params.set("startDate", startDate);
        params.set("endDate", endDate);
        if (activeAccountIds.length > 0) params.set("accountIds", activeAccountIds.join(","));
        if (flowFilter !== "all") params.set("flowFilter", flowFilter);

        const endpoint =
          dataSourceMode === "EDITABLE"
            ? "/api/transactions/snapshot/monthly-cash-flow"
            : "/api/plaid-transactions/monthly-cash-flow";

        const result = await apiJson<{ months: CashFlowMonth[] }>(
          `${endpoint}?${params}`,
          { headers: { "x-tenant-id": tenantId } },
          s.access_token,
        );
        if (cancelled) return;
        setServerChartData(result && Array.isArray(result.months) ? result : null);
      } catch (err) {
        if (cancelled) return;
        console.error("Server monthly cash flow fetch failed, falling back to client-side:", err);
        setServerChartData(null);
      } finally {
        if (!cancelled) setServerChartLoading(false);
      }
    };

    fetchServerChart();
    return () => {
      cancelled = true;
    };
  }, [
    aggregateRevision,
    isServerChartMode,
    startDate,
    endDate,
    activeAccountIds,
    flowFilter,
    dataSourceMode,
    tenantId,
    hasSession,
  ]);

  // Server Sankey aggregation fetch — runs whenever the key filters change
  // while in "flow" view.
  useEffect(() => {
    if (!isServerFlowMode || !startDate || !endDate || !hasSession || !tenantId) {
      setServerFlowData(null);
      setServerFlowLoading(false);
      return;
    }

    let cancelled = false;

    const fetchServerFlow = async () => {
      const s = sessionRef.current;
      if (!s) return;

      setServerFlowLoading(true);
      try {
        const params = new URLSearchParams();
        params.set("startDate", startDate);
        params.set("endDate", endDate);
        if (activeAccountIds.length > 0) params.set("accountIds", activeAccountIds.join(","));
        if (flowFilter !== "all") params.set("flowFilter", flowFilter);

        const endpoint =
          dataSourceMode === "EDITABLE"
            ? "/api/transactions/snapshot/flow-sankey"
            : "/api/plaid-transactions/flow-sankey";

        const result = await apiJson<SankeyAggregatedData>(
          `${endpoint}?${params}`,
          { headers: { "x-tenant-id": tenantId } },
          s.access_token,
        );
        if (cancelled) return;
        setServerFlowData(result && Array.isArray(result.categories) ? result : null);
      } catch (err) {
        if (cancelled) return;
        console.error("Server flow-sankey fetch failed, falling back to client-side:", err);
        setServerFlowData(null);
      } finally {
        if (!cancelled) setServerFlowLoading(false);
      }
    };

    fetchServerFlow();
    return () => {
      cancelled = true;
    };
  }, [
    aggregateRevision,
    isServerFlowMode,
    startDate,
    endDate,
    activeAccountIds,
    flowFilter,
    dataSourceMode,
    tenantId,
    hasSession,
  ]);

  // Note: snapshotData mapping is now handled by useMemo (mappedSnapshotData) above
  // This useEffect previously synced snapshotData -> data, but that caused stale renders
  // after bulk edits. The useMemo approach is reactive and updates immediately.

  // Edit Handlers
  const handleEditClick = (transaction: PlaidTransaction) => {
    setEditingTransaction(transaction);
    onEditModalOpen();
  };

  // Flash animation helper
  const triggerFlash = useCallback((id: string) => {
    setRecentlyEditedId(id);
    setTimeout(() => setRecentlyEditedId(null), 1500);
  }, []);

  const handleSaveTransaction = async (id: string, updates: any) => {
    try {
      await updateTransaction(id, updates);

      setData((prev) =>
        prev.map((t) => {
          if (t.id === id) {
            return {
              ...t,
              merchantName: updates.merchant || t.merchantName,
              personalFinanceCategoryPrimary:
                updates.pfc_primary || t.personalFinanceCategoryPrimary,
              personalFinanceCategoryDetailed:
                updates.pfc_detailed || t.personalFinanceCategoryDetailed,
              isModified: true,
            };
          }
          return t;
        }),
      );

      onEditModalClose();
      fetchCategories();
      triggerFlash(id);
      toast({ title: "Transaction updated", status: "success", duration: 2000 });
    } catch (error) {
      toast({ title: "Failed to update", status: "error", duration: 3000 });
    }
  };

  // Inline save handler (used by TransactionRow double-click editing)
  const handleInlineSave = async (
    id: string,
    updates: { merchant?: string; pfc_primary?: string; pfc_detailed?: string },
  ) => {
    try {
      await updateTransaction(id, updates);
      setData((prev) =>
        prev.map((t) => {
          if (t.id === id) {
            return {
              ...t,
              merchantName: updates.merchant || t.merchantName,
              personalFinanceCategoryPrimary:
                updates.pfc_primary || t.personalFinanceCategoryPrimary,
              personalFinanceCategoryDetailed:
                updates.pfc_detailed || t.personalFinanceCategoryDetailed,
              isModified: true,
            };
          }
          return t;
        }),
      );
      fetchCategories();
      invalidateServerAggregates();
      triggerFlash(id);
    } catch (error) {
      toast({ title: "Failed to update", status: "error", duration: 3000 });
    }
  };

  // Revert handler
  const handleRevert = async (transaction: PlaidTransaction) => {
    await revertTransaction(transaction.id);
    invalidateServerAggregates();
    triggerFlash(transaction.id);
  };

  // Vendor edit handler — works in both ORIGINAL and EDITABLE modes
  const handleSaveVendor = useCallback(
    async (id: string, vendor: string): Promise<void> => {
      const token = sessionRef.current?.access_token;
      if (!token) return;

      // Resolve the PlaidTransaction ID (in EDITABLE mode `id` is the ClonedTransaction UUID).
      // Any paginated table view's rows live in tableRows (one page), NOT
      // effectiveData/data/mappedSnapshotData — those full-fetch arrays are
      // skipped entirely while viewing the table (see the "Fetch Data
      // Effect" above), so they'd be empty/stale here and every lookup
      // would silently fail (EDITABLE) or make the rollback-on-failure path
      // restore the wrong value (ORIGINAL — id resolution doesn't need this
      // lookup there, but the `prev` value for rollback does).
      const lookupSource: PlaidTransaction[] = isPaginatedTableView
        ? tableRows
        : (effectiveData as PlaidTransaction[]);
      let plaidTxId: string = id;
      if (dataSourceMode === "EDITABLE") {
        const tx = lookupSource.find((t) => t.id === id);
        plaidTxId = tx?.originalPlaidTransactionId || (tx as any)?.transactionId || id;
      }

      const prev = lookupSource.find((t) => t.id === id)?.normalizedMerchant ?? null;

      // Optimistic update. For the paginated table this only updates the
      // vendorOverrides map (which effectiveData/mappedSnapshotData consumes,
      // for the OTHER EDITABLE views) — it does not touch tableRows, so the
      // visible row won't reflect the edit until the refetch below completes.
      if (dataSourceMode === "EDITABLE") {
        setVendorOverrides((o) => ({ ...o, [plaidTxId]: vendor || null }));
      } else {
        setData((d) =>
          d.map((t) => (t.id === id ? { ...t, normalizedMerchant: vendor || null } : t)),
        );
      }

      try {
        await apiJson(
          `/api/plaid-transactions/${plaidTxId}/vendor`,
          {
            method: "PATCH",
            body: JSON.stringify({ vendor }),
            ...(tenantId ? { headers: { "x-tenant-id": tenantId } } : {}),
          },
          token,
        );

        invalidateServerAggregates();

        // The paginated table doesn't read from vendorOverrides/data —
        // refetch the current page from the server so the edit actually
        // becomes visible.
        if (isSnapshotTableView) {
          fetchSnapshotPage({
            startDate,
            endDate,
            search: searchQuery,
            searchMode,
            filterMode,
            flowFilter,
            editFilter,
            sortBy: "flow_alpha",
            limit: snapshotPageSize,
            offset: (snapshotPage - 1) * snapshotPageSize,
          });
        } else if (isOriginalTableView) {
          fetchOriginalPage({ offset: (originalPage - 1) * originalPageSize });
        }
      } catch {
        // Rollback
        if (dataSourceMode === "EDITABLE") {
          setVendorOverrides((o) => {
            const next = { ...o };
            if (prev === null) delete next[plaidTxId];
            else next[plaidTxId] = prev;
            return next;
          });
        } else {
          setData((d) => d.map((t) => (t.id === id ? { ...t, normalizedMerchant: prev } : t)));
        }
        toast({
          title: "Failed to save vendor",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
        throw new Error("Failed to update vendor");
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [
      dataSourceMode,
      effectiveData,
      toast,
      isPaginatedTableView,
      isSnapshotTableView,
      isOriginalTableView,
      tableRows,
      fetchSnapshotPage,
      fetchOriginalPage,
      startDate,
      endDate,
      searchQuery,
      searchMode,
      filterMode,
      flowFilter,
      editFilter,
      snapshotPage,
      originalPage,
    ],
  );

  // 1. Calculate dynamic height based on data
  // 2. Pass explicit dimensions to Sankey to avoid flicker during scroll
  const flowDimensions = React.useMemo(() => {
    if (viewMode !== "flow" || filteredData.length === 0) return { height: 600, width: 0 };

    const expenses = filteredData.filter((t) => t.amount > 0);
    const expensesByCategory = _.groupBy(
      expenses,
      (t) => t.personalFinanceCategoryPrimary || "Other",
    );
    const categoriesCount = Object.keys(expensesByCategory).length;

    let merchantsCount = 0;
    Object.values(expensesByCategory).forEach((catTrans) => {
      merchantsCount += Object.keys(
        _.groupBy(catTrans, (t) => t.merchantName || t.name || "Unknown"),
      ).length;
    });

    const income = filteredData.filter((t) => t.amount < 0);
    const totalExpenses = _.sumBy(expenses, "amount");
    const totalIncome = Math.abs(_.sumBy(income, "amount"));
    const savingsCount = totalIncome > totalExpenses ? 1 : 0;

    const maxNodes = Math.max(1, categoriesCount + savingsCount, merchantsCount);
    // Margins + Spacing (matching nodeThickness=20 and nodeSpacing=18 in SankeyFlow)
    const minHeight = 600;
    const computedHeight = maxNodes * (20 + 18) + 120;

    return {
      height: Math.max(minHeight, computedHeight),
      width: Math.max(800, containerWidth - 10), // Subtract small padding for scrollbar stability
    };
  }, [filteredData, viewMode, containerWidth]);

  // Filtering Logic -> Moved up

  /**
   * Builds an XLSX worksheet for a payment type (checks / zelle).
   * Appends three summary rows at the bottom: Total Income, Total Expense, Net.
   * The "Amount" column always stores absolute values; direction is in "Income/Expense".
   */
  const buildPaymentSheet = (rows: Record<string, any>[], emptyRow: Record<string, any>) => {
    const data = rows.length > 0 ? rows : [emptyRow];
    const totalIncome = rows
      .filter((r) => r["Income/Expense"] === "INCOME")
      .reduce((s, r) => s + (Number(r.Amount) || 0), 0);
    const totalExpense = rows
      .filter((r) => r["Income/Expense"] === "EXPENSE")
      .reduce((s, r) => s + (Number(r.Amount) || 0), 0);
    const net = totalIncome - totalExpense;
    const firstKey = Object.keys(emptyRow)[0];
    // Blank template — all strings empty, all numbers 0 — so summary rows don't leak emptyRow text
    const blankRow = Object.fromEntries(
      Object.entries(emptyRow).map(([k, v]) => [k, typeof v === "number" ? 0 : ""]),
    );
    const separator = { ...blankRow };
    const summaryIncome = {
      ...blankRow,
      [firstKey]: "Total Income",
      Amount: totalIncome,
      "Income/Expense": "",
    };
    const summaryExpense = {
      ...blankRow,
      [firstKey]: "Total Expense",
      Amount: totalExpense,
      "Income/Expense": "",
    };
    const summaryNet = {
      ...blankRow,
      [firstKey]: "Net (Income − Expense)",
      Amount: net,
      "Income/Expense": "",
    };
    const ws = XLSX.utils.json_to_sheet([
      ...data,
      separator,
      summaryIncome,
      summaryExpense,
      summaryNet,
    ]);
    // Apply currency format to the Amount column
    const amountColIdx = Object.keys(emptyRow).indexOf("Amount");
    if (amountColIdx >= 0) {
      const range = XLSX.utils.decode_range(ws["!ref"] || "A1");
      for (let R = 1; R <= range.e.r; R++) {
        const addr = XLSX.utils.encode_cell({ r: R, c: amountColIdx });
        if (ws[addr] && typeof ws[addr].v === "number") ws[addr].z = '"$"#,##0.##;"-$"#,##0.##;-';
      }
    }
    return ws;
  };

  const handleExport = () => {
    if (loading) {
      toast({ title: "Data still loading, please wait…", status: "info", duration: 2000 });
      return;
    }

    if (filteredData.length === 0) {
      toast({ title: "No data to export", status: "warning", duration: 2000 });
      return;
    }

    const tenantName = selectedTenant?.name || "";
    const safeTenantName = tenantName
      .replace(/[^a-z0-9]/gi, "_")
      .replace(/_+/g, "_")
      .replace(/^_|_$/g, "");
    const tenantAddressParts = [
      selectedTenant?.address,
      selectedTenant?.addressLine2,
      [selectedTenant?.city, selectedTenant?.state, selectedTenant?.zipCode]
        .filter(Boolean)
        .join(", "),
    ].filter(Boolean);
    const tenantAddress = tenantAddressParts.join(", ");
    const tenantPrefix: any[][] = [[tenantName], ...(tenantAddress ? [[tenantAddress]] : []), []];
    const currencyFormat = '"$"#,##0.##;"-$"#,##0.##;-';

    const prependRowsToSheet = (ws: XLSX.WorkSheet): XLSX.WorkSheet => {
      if (!ws["!ref"]) return ws;
      const n = tenantPrefix.length;
      const range = XLSX.utils.decode_range(ws["!ref"]);
      const prefixSheet = XLSX.utils.aoa_to_sheet(tenantPrefix);
      const shifted: Record<string, XLSX.CellObject> = {};
      for (let R = range.s.r; R <= range.e.r; R++) {
        for (let C = range.s.c; C <= range.e.c; C++) {
          const src = XLSX.utils.encode_cell({ r: R, c: C });
          if (ws[src]) shifted[XLSX.utils.encode_cell({ r: R + n, c: C })] = ws[src];
        }
      }
      const result: XLSX.WorkSheet = { ...prefixSheet, ...shifted };
      result["!ref"] = XLSX.utils.encode_range({
        s: { r: 0, c: range.s.c },
        e: { r: range.e.r + n, c: range.e.c },
      });
      if (ws["!cols"]) result["!cols"] = ws["!cols"];
      return result;
    };

    const setColumnWidths = (baseData: any[], sheet: XLSX.WorkSheet) => {
      sheet["!cols"] = baseData[0].map((_: any, i: number) => {
        let maxLen = 10;
        for (let r = 0; r < Math.min(baseData.length, 50); r++) {
          const len = baseData[r][i] ? String(baseData[r][i]).length : 0;
          if (len > maxLen) maxLen = len;
        }
        return { wch: maxLen + 2 };
      });
    };

    const applyValueCurrencyFormat = (ws: XLSX.WorkSheet, startCol: number, skipRows: number) => {
      const range = XLSX.utils.decode_range(ws["!ref"] || "A1");
      for (let R = skipRows; R <= range.e.r; R++) {
        for (let C = startCol; C <= range.e.c; C++) {
          const addr = XLSX.utils.encode_cell({ r: R, c: C });
          if (ws[addr]) ws[addr].z = currencyFormat;
        }
      }
    };

    // Builds a Category sheet with two header rows: primary category (merged) + subcategory.
    const buildCategoryXactoPivotSheet = (periodLen: 4 | 7, periodHeader: string): XLSX.WorkSheet => {
      const monthGroupMap: Record<string, Record<string, number>> = {};
      const primaryCategoryMap: Record<string, Set<string>> = {};
      const groupLabels: Record<string, string> = {};

      for (const t of filteredData) {
        const period = t.date.substring(0, periodLen);
        const primary = t.personalFinanceCategoryPrimary || 'Uncategorized';
        const detailed = t.personalFinanceCategoryDetailed || 'General';
        const group = `${primary}::${detailed}`;
        groupLabels[group] = detailed;
        if (!primaryCategoryMap[primary]) primaryCategoryMap[primary] = new Set();
        primaryCategoryMap[primary].add(group);
        const accountingAmount = -(t.amount || 0);
        if (!monthGroupMap[period]) monthGroupMap[period] = {};
        monthGroupMap[period][group] = (monthGroupMap[period][group] || 0) + accountingAmount;
      }

      const groupTotals: Record<string, number> = {};
      for (const keys of Object.values(primaryCategoryMap)) {
        for (const key of keys) {
          groupTotals[key] = Object.values(monthGroupMap).reduce((s, m) => s + (m[key] || 0), 0);
        }
      }

      const primaryTotals: Record<string, number> = {};
      for (const [primary, keys] of Object.entries(primaryCategoryMap)) {
        primaryTotals[primary] = Array.from(keys).reduce((s, k) => s + (groupTotals[k] || 0), 0);
      }
      const sortedPrimaries = Object.keys(primaryCategoryMap).sort((a, b) => {
        const aIn = primaryTotals[a] > 0; const bIn = primaryTotals[b] > 0;
        if (aIn !== bIn) return aIn ? -1 : 1;
        return a.localeCompare(b);
      });

      const categoryGroups: Record<string, string[]> = {};
      const columns: string[] = [];
      for (const primary of sortedPrimaries) {
        const subkeys = Array.from(primaryCategoryMap[primary]).sort((a, b) => {
          const aIn = groupTotals[a] > 0; const bIn = groupTotals[b] > 0;
          if (aIn !== bIn) return aIn ? -1 : 1;
          return (groupLabels[a] ?? a).localeCompare(groupLabels[b] ?? b);
        });
        categoryGroups[primary] = subkeys;
        columns.push(...subkeys);
      }

      if (columns.length === 0) {
        return XLSX.utils.aoa_to_sheet([[periodHeader, 'No data for selected period']]);
      }

      const sortedPeriods = Object.keys(monthGroupMap).sort();
      const rows = sortedPeriods.map(period => {
        const pd = monthGroupMap[period];
        const rowTotal = columns.reduce((s, c) => s + (pd[c] || 0), 0);
        return { period, values: pd, rowTotal };
      });
      const colTotals: Record<string, number> = {};
      for (const col of columns) colTotals[col] = groupTotals[col];
      colTotals.grandTotal = rows.reduce((s, r) => s + r.rowTotal, 0);

      // Two header rows
      const headerRow1: any[] = [periodHeader];
      const headerRow2: any[] = [''];
      for (const primary of sortedPrimaries) {
        const subkeys = categoryGroups[primary];
        headerRow1.push(primary);
        for (let i = 1; i < subkeys.length; i++) headerRow1.push('');
        for (const subkey of subkeys) headerRow2.push(groupLabels[subkey] ?? subkey.split('::')[1] ?? subkey);
      }
      headerRow1.push('Total');
      headerRow2.push('');

      const exportData: any[] = [headerRow1, headerRow2];
      for (const row of rows) {
        const rowData: any[] = [formatPeriodLabel(row.period)];
        for (const col of columns) rowData.push(row.values[col] || 0);
        rowData.push(row.rowTotal);
        exportData.push(rowData);
      }
      const totalRow: any[] = ['Total'];
      for (const col of columns) totalRow.push(colTotals[col] || 0);
      totalRow.push(colTotals.grandTotal || 0);
      exportData.push(totalRow);

      const ws = XLSX.utils.aoa_to_sheet([...tenantPrefix, ...exportData]);

      // Merge cells for primary category headers
      const merges: XLSX.Range[] = [];
      const headerR = tenantPrefix.length;
      let colIdx = 1;
      for (const primary of sortedPrimaries) {
        const span = categoryGroups[primary].length;
        if (span > 1) {
          merges.push({ s: { r: headerR, c: colIdx }, e: { r: headerR, c: colIdx + span - 1 } });
        }
        colIdx += span;
      }
      if (merges.length > 0) ws['!merges'] = merges;

      applyValueCurrencyFormat(ws, 1, tenantPrefix.length + 2);
      setColumnWidths(exportData, ws);
      return ws;
    };

    // Builds a sheet with rows=periods, cols=groups (Xacto layout).
    const buildXactoPivotSheet = (
      groupByOption: "merchant" | "vendor" | "category" | "subcategory",
      periodLen: 4 | 7,
      rowHeader: string,
    ): XLSX.WorkSheet => {
      const matrix = computeXactoPivot(filteredData, groupByOption, periodLen);
      const { columns, rows, colTotals } = matrix;
      if (rows.length === 0) {
        return XLSX.utils.aoa_to_sheet([[rowHeader, "No data for selected period"]]);
      }
      const exportData: any[] = [];
      exportData.push([rowHeader, ...columns, "Total"]);
      rows.forEach((row) => {
        const rowData: any[] = [formatPeriodLabel(row.period)];
        columns.forEach((col) => rowData.push(row.values[col] || 0));
        rowData.push(row.rowTotal);
        exportData.push(rowData);
      });
      const totalRow: any[] = ["Total"];
      columns.forEach((col) => totalRow.push(colTotals[col] || 0));
      totalRow.push(colTotals.grandTotal || 0);
      exportData.push(totalRow);
      const ws = XLSX.utils.aoa_to_sheet([...tenantPrefix, ...exportData]);
      applyValueCurrencyFormat(ws, 1, tenantPrefix.length + 1);
      setColumnWidths(exportData, ws);
      return ws;
    };

    // Builds a transposed sheet: rows=groups, cols=months (Vendor/Merchant by Month).
    const buildTransposedByMonthSheet = (
      groupBy: "vendor" | "merchant" | "subcategory",
      rowHeader: string,
    ): XLSX.WorkSheet => {
      const matrix = computeXactoPivot(filteredData, groupBy, 7);
      const { columns, rows, colTotals } = matrix;
      if (rows.length === 0) {
        return XLSX.utils.aoa_to_sheet([[rowHeader, "No data for selected period"]]);
      }
      const exportData: any[] = [];
      exportData.push([rowHeader, ...rows.map((r) => formatPeriodLabel(r.period)), "Total"]);
      columns.forEach((col) => {
        const rowData: any[] = [col];
        rows.forEach((r) => rowData.push(r.values[col] || 0));
        rowData.push(colTotals[col] || 0);
        exportData.push(rowData);
      });
      const totalRow: any[] = ["Total"];
      rows.forEach((r) => totalRow.push(r.rowTotal));
      totalRow.push(colTotals.grandTotal || 0);
      exportData.push(totalRow);
      const ws = XLSX.utils.aoa_to_sheet([...tenantPrefix, ...exportData]);
      applyValueCurrencyFormat(ws, 1, tenantPrefix.length + 1);
      setColumnWidths(exportData, ws);
      return ws;
    };

    // Builds Category by Month transposed: rows=subcategories, cols=months.
    const buildTransposedCategoryByMonthSheet = (): XLSX.WorkSheet => {
      const monthGroupMap: Record<string, Record<string, number>> = {};
      const primaryCategoryMap: Record<string, Set<string>> = {};
      const groupLabels: Record<string, string> = {};

      for (const t of filteredData) {
        const period = t.date.substring(0, 7);
        const primary = t.personalFinanceCategoryPrimary || "Uncategorized";
        const detailed = t.personalFinanceCategoryDetailed || "General";
        const group = `${primary}::${detailed}`;
        groupLabels[group] = detailed;
        if (!primaryCategoryMap[primary]) primaryCategoryMap[primary] = new Set();
        primaryCategoryMap[primary].add(group);
        const accountingAmount = -(t.amount || 0);
        if (!monthGroupMap[period]) monthGroupMap[period] = {};
        monthGroupMap[period][group] = (monthGroupMap[period][group] || 0) + accountingAmount;
      }

      const groupTotals: Record<string, number> = {};
      for (const keys of Object.values(primaryCategoryMap)) {
        for (const key of keys) {
          groupTotals[key] = Object.values(monthGroupMap).reduce((s, m) => s + (m[key] || 0), 0);
        }
      }
      const primaryTotals: Record<string, number> = {};
      for (const [primary, keys] of Object.entries(primaryCategoryMap)) {
        primaryTotals[primary] = Array.from(keys).reduce((s, k) => s + (groupTotals[k] || 0), 0);
      }
      const sortedPrimaries = Object.keys(primaryCategoryMap).sort((a, b) => {
        const aIn = primaryTotals[a] > 0; const bIn = primaryTotals[b] > 0;
        if (aIn !== bIn) return aIn ? -1 : 1;
        return a.localeCompare(b);
      });
      const categoryGroups: Record<string, string[]> = {};
      for (const primary of sortedPrimaries) {
        categoryGroups[primary] = Array.from(primaryCategoryMap[primary]).sort((a, b) => {
          const aIn = groupTotals[a] > 0; const bIn = groupTotals[b] > 0;
          if (aIn !== bIn) return aIn ? -1 : 1;
          return (groupLabels[a] ?? a).localeCompare(groupLabels[b] ?? b);
        });
      }

      const sortedPeriods = Object.keys(monthGroupMap).sort();
      if (sortedPeriods.length === 0) {
        return XLSX.utils.aoa_to_sheet([["Category", "Subcategory", "No data for selected period"]]);
      }
      const grandTotal = sortedPeriods.reduce(
        (s, p) => s + Object.values(monthGroupMap[p]).reduce((a, v) => a + v, 0),
        0,
      );

      const exportData: any[] = [];
      exportData.push(["Category", "Subcategory", ...sortedPeriods.map(formatPeriodLabel), "Total"]);
      for (const primary of sortedPrimaries) {
        for (const subkey of categoryGroups[primary]) {
          const rowData: any[] = [primary, groupLabels[subkey] ?? subkey.split("::")[1] ?? subkey];
          sortedPeriods.forEach((p) => rowData.push(monthGroupMap[p]?.[subkey] || 0));
          rowData.push(groupTotals[subkey] || 0);
          exportData.push(rowData);
        }
      }
      const totalRow: any[] = ["", "Total"];
      sortedPeriods.forEach((p) =>
        totalRow.push(Object.values(monthGroupMap[p]).reduce((a, v) => a + v, 0)),
      );
      totalRow.push(grandTotal);
      exportData.push(totalRow);

      const ws = XLSX.utils.aoa_to_sheet([...tenantPrefix, ...exportData]);
      applyValueCurrencyFormat(ws, 2, tenantPrefix.length + 1);
      setColumnWidths(exportData, ws);
      return ws;
    };

    // Builds a sheet with rows=income/expense, cols=periods.
    const buildSummarySheet = (mData: MatrixData, periodHeader: string): XLSX.WorkSheet => {
      if (mData.rows.length === 0) {
        return XLSX.utils.aoa_to_sheet([[periodHeader, "No data for selected period"]]);
      }
      const exportData: any[] = [];
      exportData.push([periodHeader, ...mData.columns.map(formatPeriodLabel), "Total"]);
      const sortedRows = [...mData.rows].sort((a: any, b: any) => {
        if (a.label === "Income") return -1;
        if (b.label === "Income") return 1;
        return 0;
      });
      sortedRows.forEach((row: any) => {
        const mult = row.label === "Income" ? 1 : -1;
        const rowData: any[] = [row.label];
        mData.columns.forEach((p) => rowData.push((row.periods[p] || 0) * mult));
        rowData.push((row.total || 0) * mult);
        exportData.push(rowData);
      });
      const netRow: any[] = ["Net"];
      mData.columns.forEach((p) => netRow.push(mData.colTotals[p] || 0));
      netRow.push(mData.colTotals.grandTotal || 0);
      exportData.push(netRow);
      const ws = XLSX.utils.aoa_to_sheet([...tenantPrefix, ...exportData]);
      applyValueCurrencyFormat(ws, 1, tenantPrefix.length + 1);
      setColumnWidths(exportData, ws);
      return ws;
    };

    // Builds the Vendors sheet: one row per vendor, totals for the selected period.
    const buildVendorsSheet = (): XLSX.WorkSheet => {
      const byVendor = new Map<
        string,
        { vendor: string; count: number; total: number; firstDate: string; lastDate: string }
      >();
      for (const t of filteredData) {
        const vendorDisplay = getVendorDisplayLabel(t);
        const key = vendorDisplay.toLowerCase();
        const existing = byVendor.get(key);
        if (existing) {
          existing.count += 1;
          existing.total += t.amount;
          if (t.date < existing.firstDate) existing.firstDate = t.date;
          if (t.date > existing.lastDate) existing.lastDate = t.date;
        } else {
          byVendor.set(key, {
            vendor: vendorDisplay,
            count: 1,
            total: t.amount,
            firstDate: t.date,
            lastDate: t.date,
          });
        }
      }
      if (byVendor.size === 0) {
        return XLSX.utils.json_to_sheet([{ Vendor: "No vendor data for selected period" }]);
      }
      const sheetData = Array.from(byVendor.values())
        .sort((a, b) => a.vendor.localeCompare(b.vendor))
        .map((r) => ({
          Vendor: r.vendor,
          "Txn Count": r.count,
          Total: -r.total,
          "First Date": formatDateUs(r.firstDate),
          "Last Date": formatDateUs(r.lastDate),
        }));
      const ws = XLSX.utils.json_to_sheet(sheetData);
      const range = XLSX.utils.decode_range(ws["!ref"] || "A1");
      for (let R = 1; R <= range.e.r; R++) {
        const addr = XLSX.utils.encode_cell({ r: R, c: 2 });
        if (ws[addr]) ws[addr].z = currencyFormat;
      }
      ws["!cols"] = Object.keys(sheetData[0]).map((k) => {
        let max = k.length;
        for (const row of sheetData.slice(0, 50)) {
          const v = String((row as any)[k] ?? "");
          if (v.length > max) max = v.length;
        }
        return { wch: max + 2 };
      });
      return prependRowsToSheet(ws);
    };

    try {
      const fileName = `${safeTenantName ? safeTenantName + "_" : ""}Report_${startDate}_${endDate}`;
      const workbook = XLSX.utils.book_new();

      // ── Pivot sheets (rows=periods, cols=groups) ───────────────────────────
      XLSX.utils.book_append_sheet(
        workbook,
        buildTransposedByMonthSheet("vendor", "Vendor"),
        "Vendor by Month",
      );
      XLSX.utils.book_append_sheet(
        workbook,
        buildXactoPivotSheet("vendor", 4, "Year"),
        "Vendor by Year",
      );
      XLSX.utils.book_append_sheet(
        workbook,
        buildTransposedByMonthSheet("merchant", "Merchant"),
        "Merchant by Month",
      );
      XLSX.utils.book_append_sheet(
        workbook,
        buildXactoPivotSheet("merchant", 4, "Year"),
        "Merchant by Year",
      );
      XLSX.utils.book_append_sheet(
        workbook,
        buildTransposedCategoryByMonthSheet(),
        "Category by Month",
      );
      XLSX.utils.book_append_sheet(
        workbook,
        buildCategoryXactoPivotSheet(4, "Year"),
        "Category by Year",
      );
      XLSX.utils.book_append_sheet(
        workbook,
        buildTransposedByMonthSheet("subcategory", "Subcategory"),
        "Subcategory by Month",
      );
      XLSX.utils.book_append_sheet(
        workbook,
        buildXactoPivotSheet("subcategory", 4, "Year"),
        "Subcategory by Year",
      );

      // ── Summary sheets (rows=income/expense, cols=periods) ─────────────────
      XLSX.utils.book_append_sheet(
        workbook,
        buildSummarySheet(matrixMonthlySummary, "Month"),
        "Monthly Summary",
      );
      XLSX.utils.book_append_sheet(
        workbook,
        buildSummarySheet(matrixYearlySummary, "Year"),
        "Yearly Summary",
      );

      // ── Category Breakdown sheet (hierarchical: Category → Subcategory × Month) ─
      const buildCategoryBreakdownSheet = (): XLSX.WorkSheet => {
        // Collect all months and category→subcategory→month totals
        const allMonths = new Set<string>();
        const tree: Record<string, Record<string, Record<string, number>>> = {};

        for (const t of filteredData) {
          const month = t.date.substring(0, 7);
          const primary = t.personalFinanceCategoryPrimary || "Uncategorized";
          const detailed = t.personalFinanceCategoryDetailed || "(General)";
          const val = -(t.amount || 0); // accounting: income positive

          allMonths.add(month);
          if (!tree[primary]) tree[primary] = {};
          if (!tree[primary][detailed]) tree[primary][detailed] = {};
          tree[primary][detailed][month] = (tree[primary][detailed][month] || 0) + val;
        }

        const sortedMonths = Array.from(allMonths).sort();
        if (sortedMonths.length === 0) {
          return XLSX.utils.aoa_to_sheet([
            ["Category / Subcategory", "No data for selected period"],
          ]);
        }

        const header = ["Category / Subcategory", ...sortedMonths.map(formatPeriodLabel), "Total"];
        const exportData: any[] = [header];

        const sortedPrimaries = Object.keys(tree).sort();
        for (const primary of sortedPrimaries) {
          // Primary category total row
          const primaryMonthTotals: Record<string, number> = {};
          for (const detailed of Object.keys(tree[primary])) {
            for (const [month, val] of Object.entries(tree[primary][detailed])) {
              primaryMonthTotals[month] = (primaryMonthTotals[month] || 0) + val;
            }
          }
          const primaryTotal = sortedMonths.reduce((s, m) => s + (primaryMonthTotals[m] || 0), 0);
          exportData.push([
            primary,
            ...sortedMonths.map((m) => primaryMonthTotals[m] || 0),
            primaryTotal,
          ]);

          // Subcategory rows (indented with two spaces)
          const sortedDetaileds = Object.keys(tree[primary]).sort();
          for (const detailed of sortedDetaileds) {
            const subTotal = sortedMonths.reduce(
              (s, m) => s + (tree[primary][detailed][m] || 0),
              0,
            );
            exportData.push([
              `  ${detailed}`,
              ...sortedMonths.map((m) => tree[primary][detailed][m] || 0),
              subTotal,
            ]);
          }
        }

        // Grand total row
        const grandMonthTotals: Record<string, number> = {};
        for (const primary of sortedPrimaries) {
          for (const detailed of Object.keys(tree[primary])) {
            for (const [month, val] of Object.entries(tree[primary][detailed])) {
              grandMonthTotals[month] = (grandMonthTotals[month] || 0) + val;
            }
          }
        }
        const grandTotal = sortedMonths.reduce((s, m) => s + (grandMonthTotals[m] || 0), 0);
        exportData.push([
          "Total",
          ...sortedMonths.map((m) => grandMonthTotals[m] || 0),
          grandTotal,
        ]);

        const ws = XLSX.utils.aoa_to_sheet([...tenantPrefix, ...exportData]);
        applyValueCurrencyFormat(ws, 1, tenantPrefix.length + 1);
        setColumnWidths(exportData, ws);
        return ws;
      };
      XLSX.utils.book_append_sheet(workbook, buildCategoryBreakdownSheet(), "Category Breakdown");

      // ── Monthly Balance sheet (rows=months, cols=Income/Expense/Net) ────────
      const buildMonthlyBalanceSheet = (): XLSX.WorkSheet => {
        const byMonth: Record<string, { income: number; expense: number }> = {};
        for (const t of filteredDataBeforeFlow) {
          const month = t.date.substring(0, 7);
          if (!byMonth[month]) byMonth[month] = { income: 0, expense: 0 };
          if (getFlowDirection(t) === "INCOME") byMonth[month].income += Math.abs(t.amount);
          else byMonth[month].expense += Math.abs(t.amount);
        }
        const sortedMonths = Object.keys(byMonth).sort();
        if (sortedMonths.length === 0) {
          return XLSX.utils.aoa_to_sheet([
            ["Month", "Income", "Expense", "Net", "No data for selected period"],
          ]);
        }
        const exportData: any[] = [["Month", "Income", "Expense", "Net"]];
        let totalIncome = 0;
        let totalExpense = 0;
        for (const month of sortedMonths) {
          const { income, expense } = byMonth[month];
          totalIncome += income;
          totalExpense += expense;
          exportData.push([formatPeriodLabel(month), income, expense, income - expense]);
        }
        exportData.push(["Total", totalIncome, totalExpense, totalIncome - totalExpense]);
        const ws = XLSX.utils.aoa_to_sheet([...tenantPrefix, ...exportData]);
        applyValueCurrencyFormat(ws, 1, tenantPrefix.length + 1);
        setColumnWidths(exportData, ws);
        return ws;
      };
      XLSX.utils.book_append_sheet(workbook, buildMonthlyBalanceSheet(), "Monthly Balance");

      // ── Details sheet ──────────────────────────────────────────────────────
      const detailsData = filteredData.map((t) => ({
        Date: formatDateUs(t.date),
        Merchant: t.merchantName || "Unknown",
        Vendor: getVendorDisplayLabel(t),
        Name: t.name,
        Category: t.personalFinanceCategoryPrimary || "Uncategorized",
        Subcategory:
          t.personalFinanceCategoryDetailed || t.personalFinanceCategoryPrimary || "Uncategorized",
        "Category > Subcategory": (() => {
          const p = t.personalFinanceCategoryPrimary || "Uncategorized";
          const d = t.personalFinanceCategoryDetailed;
          return d && d !== p ? `${p} > ${d}` : p;
        })(),
        Amount: t.amount,
        Status: t.isModified ? "Edited" : "",
      }));
      const detailsSheet = prependRowsToSheet(XLSX.utils.json_to_sheet(detailsData));
      setColumnWidths(
        detailsData.map((d) => Object.values(d)),
        detailsSheet,
      );
      XLSX.utils.book_append_sheet(workbook, detailsSheet, "Details");

      // ── Checks sheet ───────────────────────────────────────────────────────
      const checksData = filteredData
        .filter(isCheck)
        .sort((a, b) => {
          const aIsIncome = a.amount < 0;
          const bIsIncome = b.amount < 0;
          if (aIsIncome !== bIsIncome) return aIsIncome ? -1 : 1;
          return (a.merchantName || a.name || "")
            .toLowerCase()
            .localeCompare((b.merchantName || b.name || "").toLowerCase());
        })
        .map((t) => ({
          Date: formatDateUs(t.date),
          Account: (t as any).plaidAccount?.name || "",
          Name: t.merchantName || t.name,
          Description: t.originalDescription || "",
          "Check #": getCheckNumber(t) || "",
          Amount: Math.abs(t.amount),
          "Income/Expense": getFlowDirection(t),
          Category: t.personalFinanceCategoryPrimary || "",
          Subcategory: t.personalFinanceCategoryDetailed || "",
        }));
      const checksEmptyRow = {
        Date: "",
        Account: "",
        Name: "No check transactions in selected period",
        Description: "",
        "Check #": "",
        Amount: 0,
        "Income/Expense": "",
        Category: "",
        Subcategory: "",
      };
      XLSX.utils.book_append_sheet(
        workbook,
        prependRowsToSheet(buildPaymentSheet(checksData, checksEmptyRow)),
        "Checks",
      );

      // ── Zelle sheet ────────────────────────────────────────────────────────
      const zelleData = filteredData
        .filter(isZelle)
        .sort((a, b) => {
          const aIsIncome = a.amount < 0;
          const bIsIncome = b.amount < 0;
          if (aIsIncome !== bIsIncome) return aIsIncome ? -1 : 1;
          return (a.merchantName || a.name || "")
            .toLowerCase()
            .localeCompare((b.merchantName || b.name || "").toLowerCase());
        })
        .map((t) => {
          const cp = (t as any).counterparties;
          const counterparty = Array.isArray(cp) && cp.length > 0 ? cp[0].name || "" : "";
          return {
            Date: formatDateUs(t.date),
            Account: (t as any).plaidAccount?.name || "",
            Name: t.merchantName || t.name,
            Description: t.originalDescription || "",
            Counterparty: counterparty,
            Amount: Math.abs(t.amount),
            "Income/Expense": getFlowDirection(t),
            Category: t.personalFinanceCategoryPrimary || "",
            Subcategory: t.personalFinanceCategoryDetailed || "",
          };
        });
      const zelleEmptyRow = {
        Date: "",
        Account: "",
        Name: "No Zelle transactions in selected period",
        Description: "",
        Counterparty: "",
        Amount: 0,
        "Income/Expense": "",
        Category: "",
        Subcategory: "",
      };
      XLSX.utils.book_append_sheet(
        workbook,
        prependRowsToSheet(buildPaymentSheet(zelleData, zelleEmptyRow)),
        "Zelle",
      );

      // ── Transfers sheet ───────────────────────────────────────────────────
      const transfersData = filteredData
        .filter(isTransfer)
        .sort((a, b) => {
          const aIsIncome = getFlowDirection(a) === "INCOME";
          const bIsIncome = getFlowDirection(b) === "INCOME";
          if (aIsIncome !== bIsIncome) return aIsIncome ? -1 : 1;
          return (a.merchantName || a.name || "")
            .toLowerCase()
            .localeCompare((b.merchantName || b.name || "").toLowerCase());
        })
        .map((t) => ({
          Date: formatDateUs(t.date),
          Account: (t as any).plaidAccount?.name || "",
          Name: t.merchantName || t.name,
          Description: t.originalDescription || "",
          "Transfer Type":
            t.personalFinanceCategoryDetailed || t.personalFinanceCategoryPrimary || "",
          Amount: Math.abs(t.amount),
          "Income/Expense": getFlowDirection(t),
          Category: t.personalFinanceCategoryPrimary || "",
          Subcategory: t.personalFinanceCategoryDetailed || "",
        }));
      const transfersEmptyRow = {
        Date: "",
        Account: "",
        Name: "No transfer transactions in selected period",
        Description: "",
        "Transfer Type": "",
        Amount: 0,
        "Income/Expense": "",
        Category: "",
        Subcategory: "",
      };
      XLSX.utils.book_append_sheet(
        workbook,
        prependRowsToSheet(buildPaymentSheet(transfersData, transfersEmptyRow)),
        "Transfers",
      );

      // ── Vendors sheet ──────────────────────────────────────────────────────
      XLSX.utils.book_append_sheet(workbook, buildVendorsSheet(), "Vendors");

      XLSX.writeFile(workbook, `${fileName}.xlsx`);
      toast({
        title: "Export successful",
        description: `Downloaded ${workbook.SheetNames.length} sheet(s)`,
        status: "success",
        duration: 3000,
      });
    } catch (error) {
      console.error("Export failed:", error);
      toast({ title: "Export failed", status: "error", duration: 3000 });
    }
  };

  const handleXactoCellClick = async (
    group: string | undefined,
    month: string | undefined,
    amount: number,
  ) => {
    // Helper: filter a transaction array by the group label
    const filterByGroup = (txns: PlaidTransaction[]) =>
      txns.filter((t) => {
        if (month) {
          const len = month.length; // 4 = year, 7 = month
          if (t.date.substring(0, len) !== month) return false;
        }
        if (!group) return true;
        // Each branch must derive the label exactly the way the pivot that
        // produced `group` did (useXactoMatrix). This used to call
        // getMerchantLabelForMatrix for merchants — a different function,
        // which collapses self-transfers and runs the merchant normalizer —
        // so it matched nothing and every merchant cell opened an empty
        // drill-down, on both the SQL and the client-side pivot.
        if (xactoGroupBy === "merchant") {
          return (t.merchantName || t.name || "Unknown") === group;
        }
        if (xactoGroupBy === "vendor") return getVendorLabel(t) === group;
        if (xactoGroupBy === "category") {
          const [primary, detailed] = (group || '').split('::');
          const tPrimary = t.personalFinanceCategoryPrimary || 'Uncategorized';
          const tDetailed = t.personalFinanceCategoryDetailed || 'General';
          return tPrimary === primary && tDetailed === detailed;
        }
        if (xactoGroupBy === "subcategory") {
          const tSub = t.personalFinanceCategoryDetailed || t.personalFinanceCategoryPrimary || 'Uncategorized';
          return tSub === group;
        }
        return false;
      });

    // Fast path: full data already loaded (non-pivot mode or previously loaded)
    if (data.length > 0) {
      setDrillDownData(filterByGroup(filteredData));
      setDrillDownTitle(`${group || "Grand Total"} — ${month || "All Time"}`);
      onOpen();
      return;
    }

    // Server pivot mode: no full dataset yet. Fetch just this period on-demand.
    if (!month) {
      toast({
        title: "Click a specific month cell to see details",
        status: "info",
        duration: 2000,
      });
      return;
    }

    const s = sessionRef.current;
    if (!s || !tenantId) return;

    setDrillDownLoading(true);
    try {
      const [y, m] = month.split("-").map(Number);
      const lastDay = new Date(y, m, 0).getDate();
      const periodStart = `${month}-01`;
      const periodEnd = `${month}-${String(lastDay).padStart(2, "0")}`;

      // Read back from whichever source produced the cell. This used to
      // always hit /api/plaid-transactions, so drilling into a Scenario
      // Editor cell returned live Plaid rows — the wrong dataset, without
      // any of the snapshot's edits.
      const isEditable = dataSourceMode === "EDITABLE";
      const accountsParam =
        activeAccountIds.length > 0 ? activeAccountIds.join(",") : null;

      let periodTxns: PlaidTransaction[];
      if (isEditable) {
        // The snapshot endpoint caps limit at 500 (GetSnapshotDto), so page
        // through the month rather than asking for it in one shot.
        const SNAPSHOT_PAGE = 500;
        const rows: any[] = [];
        let offset = 0;
        // eslint-disable-next-line no-constant-condition
        while (true) {
          const params = new URLSearchParams({
            startDate: periodStart,
            endDate: periodEnd,
            limit: String(SNAPSHOT_PAGE),
            offset: String(offset),
          });
          if (accountsParam) params.set("accountIds", accountsParam);

          const page = await apiJson<{ transactions: any[]; hasMore: boolean }>(
            `/api/transactions/snapshot?${params}`,
            { headers: { "x-tenant-id": tenantId } },
            s.access_token,
          );
          rows.push(...page.transactions);
          if (!page.hasMore) break;
          offset += page.transactions.length;
        }
        periodTxns = rows.map(mapClonedRow);
      } else {
        const params = new URLSearchParams({
          startDate: periodStart,
          endDate: periodEnd,
          limit: "5000",
          offset: "0",
          includePending: "false",
        });
        if (accountsParam) params.set("accountIds", accountsParam);

        const result = await apiJson<{ transactions: any[] }>(
          `/api/plaid-transactions?${params}`,
          { headers: { "x-tenant-id": tenantId } },
          s.access_token,
        );
        periodTxns = result.transactions.map(mapToPlaidTransaction);
      }

      // Apply flow filter, then group filter
      const flowFiltered =
        flowFilter === "income"
          ? periodTxns.filter((t) => getFlowDirection(t) === "INCOME")
          : flowFilter === "expense"
            ? periodTxns.filter((t) => getFlowDirection(t) === "EXPENSE")
            : periodTxns;

      setDrillDownData(filterByGroup(flowFiltered));
      setDrillDownTitle(`${group || "Grand Total"} — ${month}`);
      onOpen();
    } catch {
      toast({ title: "Failed to load transaction details", status: "error", duration: 3000 });
    } finally {
      setDrillDownLoading(false);
    }
  };

  const handleCellClick = (
    group: string | undefined,
    month: string | undefined,
    amount: number,
  ) => {
    // Filter transactions matching the group and month
    const sourceData = filteredData;

    const filtered = sourceData.filter((t) => {
      // Default matching (if month/period is provided)
      if (month) {
        if (month.length === 4) {
          // Year matching
          if (t.date.substring(0, 4) !== month) return false;
        } else {
          // Month matching
          if (t.date.substring(0, 7) !== month) return false;
        }
      }

      // Group matching logic
      if (!group) return true; // Grand Total (All Groups)

      // For month/year grouping, show all transactions in that period (no group filter)
      if (groupBy === "month" || groupBy === "year") {
        return true; // Already filtered by date above
      }

      if (groupBy === "merchant" || groupBy === "merchant_year") {
        const tMerchant = getMerchantLabelForMatrix(t);
        return tMerchant === group;
      }

      if (groupBy === "vendor" || groupBy === "vendor_year") {
        return getVendorLabel(t) === group;
      }

      if (groupBy === "category" || groupBy === "category_year") {
        // Heuristic: Check if 'group' matches Primary or Detailed.
        // IF group contains '::', it's a composite key from Matrix view
        if (group && group.includes("::")) {
          const [cat, sub] = group.split("::");
          // Strict match
          return (
            t.personalFinanceCategoryPrimary === cat && t.personalFinanceCategoryDetailed === sub
          );
        }

        // Fallback (if passed usually)
        const primary = t.personalFinanceCategoryPrimary || "Uncategorized";
        const detailed = t.personalFinanceCategoryDetailed || "Uncategorized";
        return primary === group || detailed === group;
      }

      return false;
    });

    setDrillDownData(filtered);
    // Display cleaner title
    const displayTitle =
      group && group.includes("::") ? group.replace("::", " > ") : group || "Grand Total";
    setDrillDownTitle(`${displayTitle} - ${month || "All Time"}`);
    onOpen();
  };

  // Keep external export ref up to date
  useEffect(() => {
    if (onExportRef) onExportRef.current = handleExport;
  });

  // Export drill-down data to CSV
  const handleExportDrillDown = () => {
    if (!drillDownData || drillDownData.length === 0) {
      toast({
        title: "No data to export",
        description: "The current selection has no transactions",
        status: "warning",
        duration: 2000,
      });
      return;
    }

    try {
      // Create workbook
      const workbook = XLSX.utils.book_new();

      // Prepare data for CSV export
      const csvData = drillDownData.map((t) => ({
        Date: formatDateUs(t.date),
        Merchant: t.merchantName || t.name || "Unknown",
        "Normalized Merchant": getVendorDisplayLabel(t),
        Description: t.name || "",
        Category: t.personalFinanceCategoryPrimary || "Uncategorized",
        Subcategory: t.personalFinanceCategoryDetailed || "Uncategorized",
        Amount: t.amount,
        Currency: t.isoCurrencyCode || "USD",
        Account: t.plaidAccount?.name || "Unknown",
        Status: t.isModified ? "Edited" : "Original",
        "Payment Channel": t.paymentChannel || "",
      }));

      // Create worksheet
      const worksheet = XLSX.utils.json_to_sheet(csvData);

      // Auto-calculate column widths
      const colWidths = Object.keys(csvData[0] || {}).map((key) => {
        let maxLen = key.length;
        for (const row of csvData.slice(0, 50)) {
          const val = String(row[key as keyof typeof row] || "");
          if (val.length > maxLen) maxLen = val.length;
        }
        return { wch: maxLen + 2 };
      });
      worksheet["!cols"] = colWidths;

      // Add worksheet to workbook
      XLSX.utils.book_append_sheet(workbook, worksheet, "Transactions");

      // Generate filename based on current filter
      const sanitizedTitle = drillDownTitle
        .replace(/[^a-zA-Z0-9\s-]/g, "")
        .replace(/\s+/g, "_")
        .substring(0, 50);
      const fileName = `Transactions_${sanitizedTitle}_${new Date().toISOString().split("T")[0]}`;

      downloadWorkbookAsCsv(workbook, fileName);

      toast({
        title: "Export successful",
        description: `Downloaded ${drillDownData.length} transactions`,
        status: "success",
        duration: 3000,
      });
    } catch (error) {
      console.error("Export failed:", error);
      toast({
        title: "Export failed",
        description: toUserMessage(error, "Please try again in a moment."),
        status: "error",
        duration: 3000,
      });
    }
  };

  // Helper to format currency
  const formatCurrency = (val: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
    }).format(val);
  };

  return (
    <VStack
      spacing={6}
      align="stretch"
      // Flex items default to `min-height: auto`, i.e. they refuse to
      // shrink below their content's height even when flex-shrink allows
      // it and the flex column parent is bounded — the classic flexbox
      // "child ignores its container's height" trap. Without minH={0}
      // here, containedScroll's bounded ancestors (TenantTransactionsV2Page
      // / SnapshotPage's `overflow=hidden` wrapper) can't actually
      // constrain this VStack, so it grows to fit all 100 rows and gets
      // silently clipped by that ancestor's `overflow: hidden` — no
      // scrollbar anywhere, and the pager (rendered below the table)
      // ends up entirely unreachable. See
      // docs/plans/UI_LAYOUT_DENSITY_PLAN.md §H4 / the containedScroll
      // prop doc on TransactionTable.
      minH={containedScroll ? 0 : undefined}
    >
      {/* Toolbar */}
      {!hideControls && (
        <Box position="sticky" top="0" zIndex="10" pb={4}>
          {/* Bulk Selection Toolbar */}
          {drillSelectedIds.size > 0 &&
          dataSourceMode === "EDITABLE" &&
          (viewMode === "table" || viewMode === "grouped") ? (
            <Card variant="solid" bg="orange.500" color="white" shadow="md">
              <CardBody py={3}>
                <HStack justify="space-between">
                  <HStack>
                    <CheckIcon boxSize={4} />
                    <Text fontWeight="bold">{drillSelectedIds.size} selected</Text>
                  </HStack>
                  <HStack>
                    <Button
                      size="sm"
                      bg="white"
                      color="orange.600"
                      leftIcon={<EditIcon />}
                      onClick={onBulkEditOpen}
                      _hover={{ bg: "gray.100" }}
                    >
                      Bulk Edit
                    </Button>
                    <IconButton
                      aria-label="Clear selection"
                      icon={<CloseIcon />}
                      size="sm"
                      variant="ghost"
                      colorScheme="whiteAlpha"
                      onClick={() => {
                        setDrillSelectedIds(new Set());
                        setDrillSelectAllMode("none");
                      }}
                    />
                  </HStack>
                </HStack>
              </CardBody>
            </Card>
          ) : (
            <Card variant="outline">
              <CardBody py={4}>
                <HStack spacing={4} wrap="wrap" align="flex-end">
                  <Box>
                    <Heading size="xs" mb={1} color="gray.500">
                      Period
                    </Heading>
                    <MonthYearPicker
                      startDate={startDate}
                      endDate={endDate}
                      onRangeSelect={(start, end) => {
                        setStartDate(start);
                        setEndDate(end);
                      }}
                    />
                  </Box>

                  <Box>
                    <Heading size="xs" mb={1} color="gray.500">
                      Account
                    </Heading>
                    <AccountSelector
                      values={activeAccountIds}
                      onChangeMulti={handleAccountIdsChange}
                      multiSelect
                      compact
                      size="sm"
                    />
                  </Box>

                  {!forcedDataSourceMode && (
                    <Box>
                      <Heading size="xs" mb={1} color="gray.500">
                        Data Source
                      </Heading>
                      <ButtonGroup size="sm" isAttached variant="outline">
                        <Button
                          colorScheme={dataSourceMode === "ORIGINAL" ? "blue" : "gray"}
                          variant={dataSourceMode === "ORIGINAL" ? "solid" : "outline"}
                          onClick={() => setInternalMode("ORIGINAL")}
                        >
                          Live (Read-Only)
                        </Button>
                        <Button
                          colorScheme={dataSourceMode === "EDITABLE" ? "orange" : "gray"}
                          variant={dataSourceMode === "EDITABLE" ? "solid" : "outline"}
                          onClick={() => setInternalMode("EDITABLE")}
                          leftIcon={<Icon as={EditIcon} />}
                        >
                          Snapshot (Editable)
                        </Button>
                      </ButtonGroup>
                    </Box>
                  )}
                  {dataSourceMode === "EDITABLE" && (
                    <HStack mt={1}>
                      <Button
                        size="xs"
                        onClick={initSnapshot}
                        isLoading={snapshotLoading}
                        colorScheme="green"
                      >
                        Refresh/Init
                      </Button>
                      <Button
                        size="xs"
                        onClick={resetSnapshot}
                        isLoading={snapshotLoading}
                        colorScheme="red"
                        variant="ghost"
                      >
                        Reset
                      </Button>
                    </HStack>
                  )}

                  <Box>
                    <Heading size="xs" mb={1} color="gray.500">
                      View
                    </Heading>
                    <ButtonGroup size="sm" isAttached variant="outline">
                      <Button
                        colorScheme={viewMode === "table" ? "blue" : "gray"}
                        variant={viewMode === "table" ? "solid" : "outline"}
                        onClick={() => setViewMode("table")}
                        leftIcon={<Icon as={MdViewList} />}
                      >
                        Table
                      </Button>
                      <Button
                        colorScheme={viewMode === "pivot" ? "blue" : "gray"}
                        variant={viewMode === "pivot" ? "solid" : "outline"}
                        onClick={() => setViewMode("pivot")}
                        leftIcon={<Icon as={MdGridOn} />}
                      >
                        Pivot
                      </Button>
                      {!hideFlowView && (
                        <Button
                          colorScheme={viewMode === "flow" ? "blue" : "gray"}
                          variant={viewMode === "flow" ? "solid" : "outline"}
                          onClick={() => setViewMode("flow")}
                          leftIcon={<Icon as={MdAccountTree} />}
                        >
                          Flow
                        </Button>
                      )}
                      <Button
                        colorScheme={viewMode === "chart" ? "blue" : "gray"}
                        variant={viewMode === "chart" ? "solid" : "outline"}
                        onClick={() => setViewMode("chart")}
                        leftIcon={<Icon as={MdShowChart} />}
                      >
                        Balance Flow
                      </Button>
                      <Button
                        colorScheme={viewMode === "payments" ? "blue" : "gray"}
                        variant={viewMode === "payments" ? "solid" : "outline"}
                        onClick={() => setViewMode("payments")}
                        leftIcon={<Icon as={MdPayment} />}
                      >
                        Checks / Zelle
                      </Button>
                      <Button
                        colorScheme={viewMode === "grouped" ? "blue" : "gray"}
                        variant={viewMode === "grouped" ? "solid" : "outline"}
                        onClick={() => setViewMode("grouped")}
                        leftIcon={<Icon as={MdViewList} />}
                      >
                        Grouped
                      </Button>
                    </ButtonGroup>
                  </Box>

                  {viewMode === "pivot" && (
                    <Box minW="200px">
                      <Heading size="xs" mb={1} color="gray.500">
                        Group by
                      </Heading>
                      <Select
                        size="sm"
                        value={groupBy}
                        onChange={(e) => setGroupBy(e.target.value as GroupByOption)}
                      >
                        <option value="vendor">Vendor (Month)</option>
                        <option value="vendor_year">Vendor (Year)</option>
                        <option value="merchant">Merchant (Month)</option>
                        <option value="merchant_year">Merchant (Year)</option>
                        <option value="category">Category (Month)</option>
                        <option value="category_year">Category (Year)</option>
                        <option value="month">Month (Total)</option>
                        <option value="year">Year (Total)</option>
                      </Select>
                    </Box>
                  )}

                  {viewMode === "grouped" && (
                    <Box>
                      <Heading size="xs" mb={1} color="gray.500">
                        Grouped Mode
                      </Heading>
                      <ButtonGroup size="sm" isAttached variant="outline">
                        <Button
                          colorScheme={groupedMode === "consolidated" ? "blue" : "gray"}
                          variant={groupedMode === "consolidated" ? "solid" : "outline"}
                          onClick={() => setGroupedMode("consolidated")}
                          fontSize="xs"
                        >
                          Consolidated
                        </Button>
                        <Button
                          colorScheme={groupedMode === "monthly" ? "blue" : "gray"}
                          variant={groupedMode === "monthly" ? "solid" : "outline"}
                          onClick={() => setGroupedMode("monthly")}
                          fontSize="xs"
                        >
                          Monthly Breakdown
                        </Button>
                      </ButtonGroup>
                    </Box>
                  )}

                  <Box minW="280px" flex={1}>
                    <Heading size="xs" mb={1} color="gray.500">
                      Search
                    </Heading>
                    <HStack spacing={0}>
                      <Button
                        size="sm"
                        borderRightRadius={0}
                        colorScheme={searchMode === "merchant" ? "blue" : "gray"}
                        variant={searchMode === "merchant" ? "solid" : "outline"}
                        onClick={() => setSearchMode("merchant")}
                        px={4}
                        fontSize="xs"
                      >
                        Merchant
                      </Button>
                      <Button
                        size="sm"
                        borderLeftRadius={0}
                        borderLeft="none"
                        colorScheme={searchMode === "category" ? "purple" : "gray"}
                        variant={searchMode === "category" ? "solid" : "outline"}
                        onClick={() => setSearchMode("category")}
                        px={4}
                        fontSize="xs"
                      >
                        Category
                      </Button>
                      <Input
                        ml={2}
                        size="sm"
                        placeholder={`Search ${searchMode}...`}
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        borderRadius="md"
                      />
                    </HStack>
                  </Box>

                  <Box>
                    <Heading size="xs" mb={1} color="gray.500">
                      Flow
                    </Heading>
                    <ButtonGroup size="sm" isAttached variant="outline">
                      <Button
                        colorScheme={flowFilter === "all" ? "blue" : "gray"}
                        variant={flowFilter === "all" ? "solid" : "outline"}
                        onClick={() => setFlowFilter("all")}
                        fontSize="xs"
                      >
                        All
                      </Button>
                      <Button
                        colorScheme={flowFilter === "income" ? "green" : "gray"}
                        variant={flowFilter === "income" ? "solid" : "outline"}
                        onClick={() => setFlowFilter("income")}
                        fontSize="xs"
                      >
                        Income
                      </Button>
                      <Button
                        colorScheme={flowFilter === "expense" ? "red" : "gray"}
                        variant={flowFilter === "expense" ? "solid" : "outline"}
                        onClick={() => setFlowFilter("expense")}
                        fontSize="xs"
                      >
                        Expense
                      </Button>
                    </ButtonGroup>
                  </Box>

                  {dataSourceMode === "EDITABLE" && (
                    <Box>
                      <Heading size="xs" mb={1} color="gray.500">
                        Status
                      </Heading>
                      <ButtonGroup size="sm" isAttached variant="outline">
                        <Button
                          colorScheme={editFilter === "all" ? "blue" : "gray"}
                          variant={editFilter === "all" ? "solid" : "outline"}
                          onClick={() => setEditFilter("all")}
                          fontSize="xs"
                        >
                          All
                        </Button>
                        <Button
                          colorScheme={editFilter === "edited" ? "orange" : "gray"}
                          variant={editFilter === "edited" ? "solid" : "outline"}
                          onClick={() => setEditFilter("edited")}
                          fontSize="xs"
                        >
                          Edited {editedCount > 0 && `(${editedCount})`}
                        </Button>
                        <Button
                          colorScheme={editFilter === "original" ? "gray" : "gray"}
                          variant={editFilter === "original" ? "solid" : "outline"}
                          onClick={() => setEditFilter("original")}
                          fontSize="xs"
                        >
                          Original {originalCount > 0 && `(${originalCount})`}
                        </Button>
                      </ButtonGroup>
                    </Box>
                  )}

                  {/* AI Analysis Button - Only available in Pivot mode with strict grouping rules */}
                  {viewMode === "pivot" &&
                    ["merchant", "merchant_year", "category", "category_year"].includes(
                      groupBy,
                    ) && (
                      <Button
                        leftIcon={<Icon as={FaBrain} />}
                        onClick={onAiPanelOpen}
                        size="sm"
                        colorScheme="purple"
                        variant="outline"
                      >
                        AI Analysis
                      </Button>
                    )}

                  <Button
                    leftIcon={<Icon as={MdFileDownload} />}
                    onClick={handleExport}
                    size="sm"
                    colorScheme="green"
                    variant="outline"
                    {...exportButtonProps}
                  >
                    {exportButtonLabel ||
                      (viewMode === "pivot" || viewMode === "flow"
                        ? "Export (Pivot Format)"
                        : "Export")}
                  </Button>
                </HStack>
                {viewMode === "grouped" && (
                  <Text mt={2} fontSize="xs" color="gray.500">
                    Grouped export is always consolidated by merchant for the selected period.
                  </Text>
                )}
              </CardBody>
            </Card>
          )}
        </Box>
      )}

      {/* Main Content */}
      <Box
        flex={containedScroll ? 1 : undefined}
        minH={containedScroll ? 0 : undefined}
        display={containedScroll ? "flex" : undefined}
        flexDirection={containedScroll ? "column" : undefined}
        overflow={containedScroll ? "hidden" : undefined}
      >
      {(() => {
        if (isServerPivotMode) return serverPivotLoading;
        if (isServerChartMode) return serverChartLoading;
        if (isServerFlowMode) return serverFlowLoading;
        if (isSnapshotTableView) {
          // Table view: loading tracks the page fetch only, not the (unused,
          // for this view) full-snapshot fetch.
          return snapshotPageLoading && tableRows.length === 0;
        }
        if (isOriginalTableView) {
          return originalPageLoading && tableRows.length === 0;
        }
        if (dataSourceMode === "EDITABLE") return snapshotLoading;
        return loading;
      })() ? (
        <Center p={10}>
          <Spinner size="xl" thickness="4px" speed="0.65s" emptyColor="gray.200" color="blue.500" />
        </Center>
      ) : (isServerPivotMode
          ? !serverPivotData
          : isServerChartMode
            ? (serverChartData?.months.length ?? 0) === 0 && filteredData.length === 0
            : isServerFlowMode
              ? (serverFlowData?.categories.length ?? 0) === 0 &&
                (serverFlowData?.totalIncome ?? 0) === 0 &&
                filteredData.length === 0
              : isPaginatedTableView
                ? tableRows.length === 0
                : filteredData.length === 0
        ) ? (
        <Center p={16}>
          <VStack spacing={2}>
            {editFilter === "edited" ? (
              <>
                <Text fontSize="lg" fontWeight="500" color="gray.500">
                  No edited transactions
                </Text>
                <Text fontSize="sm" color="gray.400">
                  Edit a transaction to see it here
                </Text>
              </>
            ) : editFilter === "original" ? (
              <>
                <Text fontSize="lg" fontWeight="500" color="gray.500">
                  No original (unedited) transactions
                </Text>
                <Text fontSize="sm" color="gray.400">
                  All transactions have been edited
                </Text>
              </>
            ) : (
              <>
                <Text fontSize="lg" fontWeight="500" color="gray.500">
                  No transactions in this period
                </Text>
                <Text fontSize="sm" color="gray.400">
                  {startDate} – {endDate}
                </Text>
              </>
            )}
          </VStack>
        </Center>
      ) : viewMode === "table" ? (
        <Box
          flex={containedScroll ? 1 : undefined}
          minH={containedScroll ? 0 : undefined}
          display={containedScroll ? "flex" : undefined}
          flexDirection={containedScroll ? "column" : undefined}
          w="full"
        >
          <TransactionTable
            containedScroll={containedScroll}
            transactions={tableRows}
            selectedIds={drillSelectedIds}
            selectAllMode={drillSelectAllMode}
            totalCount={tableTotalCount}
            sort={drillSort}
            onToggleSort={(col) => {
              setDrillSort((prev) => ({
                column: col,
                direction: prev.column === col && prev.direction === "desc" ? "asc" : "desc",
              }));
            }}
            onSelectTransaction={(id) => {
              setDrillSelectedIds((prev) => {
                const newSet = new Set(prev);
                if (newSet.has(id)) newSet.delete(id);
                else newSet.add(id);
                return newSet;
              });
            }}
            onSelectAllDisplayed={() => {
              if (drillSelectedIds.size === tableRows.length && tableRows.length > 0) {
                setDrillSelectedIds(new Set());
                setDrillSelectAllMode("none");
              } else {
                setDrillSelectedIds(new Set(tableRows.map((t) => t.id)));
                setDrillSelectAllMode("displayed");
              }
            }}
            onSelectAll={() => {
              setDrillSelectedIds(new Set(tableRows.map((t) => t.id)));
              setDrillSelectAllMode("all");
            }}
            onEdit={handleEditClick}
            onInlineSave={handleInlineSave}
            onRevert={handleRevert}
            onSaveVendor={handleSaveVendor}
            isEditable={dataSourceMode === "EDITABLE"}
            availableCategories={dbCategories}
            recentlyEditedId={recentlyEditedId}
            disableCrossPageSelectAll={isPaginatedTableView}
            onEditingActiveChange={(commit) => {
              pendingRowCommitRef.current = commit;
            }}
          />
          {/* Server-side pagination for the table view (both data sources).
              Non-table views are a separate, later phase of this plan
              (still the full client-filtered array) and have no page
              concept yet, so the pager only renders here. Selection
              ("Select all") is intentionally scoped to the current page
              only (see disableCrossPageSelectAll above) — a true
              cross-page "select every row matching these filters" bulk
              action needs a filter-based bulk endpoint the backend doesn't
              have yet, so the UI doesn't promise a scope it can't deliver. */}
          {isPaginatedTableView && (
            <HStack
              justify="space-between"
              mt={containedScroll ? 0 : 4}
              pt={containedScroll ? 2 : 0}
              px={1}
              wrap="wrap"
              rowGap={2}
              flexShrink={0}
              borderTopWidth={containedScroll ? "1px" : 0}
              borderColor="border.default"
            >
              <HStack spacing={4} wrap="wrap" rowGap={2}>
                <Text fontSize="sm" color="gray.500">
                  {tableTotalCount === 0
                    ? "No transactions"
                    : isSnapshotTableView
                      ? `Showing ${(snapshotPage - 1) * snapshotPageSize + 1}–${Math.min(
                          snapshotPage * snapshotPageSize,
                          tableTotalCount,
                        )} of ${tableTotalCount.toLocaleString()}`
                      : `Showing ${(originalPage - 1) * originalPageSize + 1}–${Math.min(
                          originalPage * originalPageSize,
                          tableTotalCount,
                        )} of ${tableTotalCount.toLocaleString()}`}
                </Text>
                <PageSizeSelect
                  pageSize={isSnapshotTableView ? snapshotPageSize : originalPageSize}
                  isDisabled={isSnapshotTableView ? snapshotPageLoading : originalPageLoading}
                  onChange={async (size) => {
                    // Changing page size replaces the rendered row set just
                    // like changing page does — flush a pending edit first
                    // for the same R2-A reason (see TablePagination above).
                    const ok = await flushPendingRowEdit();
                    if (!ok) return;
                    if (isSnapshotTableView) setSnapshotPageSize(size);
                    else setOriginalPageSize(size);
                  }}
                />
              </HStack>
              {isSnapshotTableView ? (
                <TablePagination
                  page={snapshotPage}
                  totalPages={snapshotTotalPages}
                  onPageChange={setSnapshotPage}
                  onBeforePageChange={flushPendingRowEdit}
                  isLoading={snapshotPageLoading}
                />
              ) : (
                <TablePagination
                  page={originalPage}
                  totalPages={originalTotalPages}
                  onPageChange={setOriginalPage}
                  onBeforePageChange={flushPendingRowEdit}
                  isLoading={originalPageLoading}
                />
              )}
            </HStack>
          )}
        </Box>
      ) : viewMode === "flow" ? (
        <Box
          ref={flowContainerRef}
          maxH="82vh"
          overflowY="scroll"
          pr={2}
          sx={{ scrollbarGutter: "stable" }}
        >
          <SankeyFlow
            data={filteredData}
            aggregatedData={serverFlowData ?? undefined}
            width={flowDimensions.width || 800}
            height={flowDimensions.height}
          />
        </Box>
      ) : viewMode === "chart" ? (
        <CashFlowChart months={serverChartData?.months ?? monthlyChartData} />
      ) : viewMode === "payments" ? (
        <PaymentsReport
          transactions={
            filterMode === "checks" || filterMode === "zelle" || filterMode === "transfer"
              ? effectiveData
              : filteredData
          }
          initialTab={
            filterMode === "zelle" ? "zelle" : filterMode === "transfer" ? "transfer" : "checks"
          }
          hideTabs={filterMode === "checks" || filterMode === "zelle" || filterMode === "transfer"}
          isEditable={dataSourceMode === "EDITABLE"}
          onEdit={handleEditClick}
          onInlineSave={handleInlineSave}
          onRevert={handleRevert}
          onSaveVendor={handleSaveVendor}
          availableCategories={dbCategories}
          recentlyEditedId={recentlyEditedId}
        />
      ) : viewMode === "grouped" ? (
        <MerchantGroupedView
          rows={merchantGroupedRows}
          mode={groupedMode}
          isEditable={dataSourceMode === "EDITABLE"}
          selectedIds={drillSelectedIds}
          onRowClick={(transactions, title) => {
            setDrillDownData(transactions);
            setDrillDownTitle(title);
            onOpen();
          }}
          onGroupSelect={(ids) => {
            setDrillSelectedIds(new Set(ids));
          }}
        />
      ) : viewMode === "pivot" && useXactoLayout ? (
        <>
          {drillDownLoading && (
            <Center py={3}>
              <Spinner size="md" color="blue.500" />
            </Center>
          )}
          <XactoPivot
            xactoData={serverPivotData ?? xactoMatrixData}
            groupBy={xactoGroupBy}
            onCellClick={handleXactoCellClick}
            orientation={pivotOrientation}
            onExport={hideControls ? handleExport : undefined}
            showToolbarActions={!hideControls}
          />
        </>
      ) : (
        <FinancialPivot
          groupBy={groupBy}
          matrixData={matrixData}
          onCellClick={handleCellClick}
          orientation={pivotOrientation}
        />
      )}
      </Box>

      {/* Edit Modal */}
      <EditTransactionModal
        isOpen={isEditModalOpen}
        onClose={onEditModalClose}
        transaction={editingTransaction}
        onSave={handleSaveTransaction}
        availableCategories={dbCategories}
      />

      <BulkEditModal
        isOpen={isBulkEditOpen}
        onClose={onBulkEditClose}
        selectedCount={drillSelectedIds.size}
        onSave={handleBulkSave}
        availableCategories={dbCategories}
      />

      {/* Drill-down Drawer */}
      <Drawer isOpen={isOpen} onClose={onClose} size="full" placement="right">
        <DrawerOverlay />
        <DrawerContent maxW="90vw">
          <DrawerHeader borderBottomWidth="1px">
            <HStack justify="space-between" align="center" pr={10}>
              <Text>Details: {drillDownTitle}</Text>
              <Button
                leftIcon={<Icon as={MdFileDownload} />}
                onClick={handleExportDrillDown}
                size="sm"
                colorScheme="green"
                variant="outline"
              >
                Export CSV
              </Button>
            </HStack>
          </DrawerHeader>
          <DrawerCloseButton />
          <DrawerBody p={0}>
            <Box p={4} bg="bg.primary" minH="100%">
              {/* Reuse TransactionTable */}
              <TransactionTable
                transactions={drillDownData}
                selectedIds={drillSelectedIds}
                selectAllMode={drillSelectAllMode}
                totalCount={drillDownData.length}
                sort={drillSort}
                onToggleSort={(col) => {
                  setDrillSort((prev) => ({
                    column: col,
                    direction: prev.column === col && prev.direction === "desc" ? "asc" : "desc",
                  }));
                  // Sort in-place for the modal view
                  const sorted = [...drillDownData].sort((a, b) => {
                    const dir = drillSort.direction === "asc" ? 1 : -1;
                    const valA = (a as any)[col];
                    const valB = (b as any)[col];
                    if (valA < valB) return -1 * dir;
                    if (valA > valB) return 1 * dir;
                    return 0;
                  });
                  setDrillDownData(sorted);
                }}
                onSelectTransaction={(id) => {
                  setDrillSelectedIds((prev) => {
                    const newSet = new Set(prev);
                    if (newSet.has(id)) newSet.delete(id);
                    else newSet.add(id);
                    return newSet;
                  });
                }}
                onSelectAllDisplayed={() => {
                  if (drillSelectedIds.size === drillDownData.length && drillDownData.length > 0) {
                    setDrillSelectedIds(new Set());
                    setDrillSelectAllMode("none");
                  } else {
                    setDrillSelectedIds(new Set(drillDownData.map((t) => t.id)));
                    setDrillSelectAllMode("displayed");
                  }
                }}
                onSelectAll={() => {
                  setDrillSelectedIds(new Set(drillDownData.map((t) => t.id)));
                  setDrillSelectAllMode("all");
                }}
                onEdit={handleEditClick}
                onInlineSave={handleInlineSave}
                onRevert={handleRevert}
                onSaveVendor={handleSaveVendor}
                isEditable={dataSourceMode === "EDITABLE"}
                availableCategories={dbCategories}
                recentlyEditedId={recentlyEditedId}
              />

              {/* Total Footer (Custom for Drawer) */}
              <Box
                mt={4}
                p={4}
                bg="bg.muted"
                borderRadius="md"
                borderLeft="4px solid"
                borderLeftColor="xacto.electricCyan"
              >
                <HStack justify="space-between">
                  <Text fontWeight="bold" color="text.primary">
                    Total Amount
                  </Text>
                  <Text fontWeight="bold" fontSize="lg" color="accent.primary">
                    {formatCurrency(drillDownData.reduce((sum, t) => sum + (t.amount || 0), 0))}
                  </Text>
                </HStack>
              </Box>
            </Box>
          </DrawerBody>
        </DrawerContent>
      </Drawer>

      {/* AI Analysis Panel (period-based) — always mounted to preserve conversation state */}
      <AiAnalysisPanel
        isOpen={isAiPanelOpen}
        onClose={onAiPanelClose}
        matrixData={xactoMatrixData}
        groupBy={xactoGroupBy}
        period={startDate && endDate ? { start: startDate, end: endDate } : undefined}
        viewMode={viewMode as any}
        grouping={
          groupBy === "merchant"
            ? "merchant-month"
            : groupBy === "merchant_year"
              ? "merchant-year"
              : groupBy === "category"
                ? "category-month"
                : groupBy === "category_year"
                  ? "category-year"
                  : undefined
        }
      />
    </VStack>
  );
};
